// React & Libraries
import React, { useEffect, Fragment, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import Lottie from 'lottie-react';

// Ant Design
import { Button, DatePicker, Drawer, notification, Popconfirm } from 'antd';

// FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faSquareUpRight } from '@fortawesome/free-solid-svg-icons';

// Constants & Helpers
import { INFINITE_EXPIRY_DATE, QUANTITYROUND, toISTDate, QUANTITY } from '@Apis/constants';
import Helpers from '@Apis/helpers';
import CustomFieldHelpers from '@Helpers/CustomFieldHelpers';
import FormHelpers from '@Helpers/FormHelpers';

// UI Components
import H3Text from '@Uilib/h3Text';
import H3FormInput from '@Uilib/h3FormInput';
import H3Button, { defaultButtonTypes } from '@Uilib/h3Button';
import H3Image from '@Uilib/h3Image';
import CustomFieldLine from '@Components/Common/CustomFieldLine';
import SelectAppUser from '@Components/Common/SelectAppUser';

// Domain Components
import BatchesList from '@Components/Common/BatchesList';
import WarehouseLocationSelector from '../../../../Common/Selector/WarehouseLocationSelector';
import UpdateQCCustomInputs from '../../../../Purchase/GoodsReceiving/GoodsReceivingHome/ApproveGRNQC/UpdateQCCustomInputs';

// Actions
import QualityCheckActions from '@Actions/quality/qualityCheckActions';
import CFV2Actions from '@Actions/configurations/cfV2Actions';

// Assets
import closeIcon from '@Images/icons/icon-close-blue.png';
import Crown from '@Images/crown2.png';
import qcLottie from '../../../../../media/lotties/qc.json';

// Styles
import '../../../../../scss/components/qcApproval.scss';
import './style.scss';


const ApproveQC = ({
  user, selectedQC, updateQualityCheck, updateQualityCheckLoading, callback, selectedGRN, onClose, org,
  putQualityCheckOnHold, putQualityCheckOnHoldLoading, priceMasking, selectedMO, getDocCFV2, cfV2DocGoodReceivingNotes, isCarryForward
}) => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [copySelectedQC, setCopySelectedQC] = useState({});
  const [approvedQty, setApprovedQty] = useState(0);
  const [rejectedQty, setRejectedQty] = useState(0);
  const [showLineBatches, setShowLineBatches] = useState(false);
  const [selectedLineInfo, setSelectedLineInfo] = useState(null);
  const [availableBatches, setAvailableBatches] = useState(null);
  const [selectedBatch, setNewSelectedBatch] = useState(null);
  const [nextBatchCode, setNextBatchCode] = useState(null);
  const [additionalInputsData, setAdditionalInputsData] = useState([]);
  const [approveArNumber, setApproveArNumber] = useState('');
  const [rejectedArNumber, setRejectedArNumber] = useState('');
  const [approveLotNumber, setApproveLotNumber] = useState('');
  const [rejectedLotNumber, setRejectedLotNumber] = useState('');
  const [approveMRP, setApproveMRP] = useState('');
  const [rejectedMRP, setRejectedMRP] = useState('');
  const [approveCostPrice, setApproveCostPrice] = useState('');
  const [rejectedCostPrice, setRejectedCostPrice] = useState('');
  const [approveSellingPrice, setApproveSellingPrice] = useState('');
  const [rejectedSellingPrice, setRejectedSellingPrice] = useState('');
  const [approveRollNumber, setApproveRollNumber] = useState('');
  const [rejectedRollNumber, setRejectedRollNumber] = useState('');
  const [approveLandedCost, setApproveLandedCost] = useState('');
  const [rejectedLandedCost, setRejectedLandedCost] = useState('');
  const [approveFreightCost, setApproveFreightCost] = useState('');
  const [rejectedFreightCost, setRejectedFreightCost] = useState('');
  const [approveOtherCost, setApproveOtherCost] = useState('');
  const [rejectedOtherCost, setRejectedOtherCost] = useState('');
  const [approveBranded, setApproveBranded] = useState('');
  const [rejectedBranded, setRejectedBranded] = useState('');
  const [approveMfgBatchNumber, setApproveMfgBatchNumber] = useState('');
  const [rejectedMfgBatchNumber, setRejectedMfgBatchNumber] = useState('');
  const [approveMfgDate, setApproveMfgDate] = useState('');
  const [rejectedMfgDate, setRejectedMfgDate] = useState('');
  const [approveMargin, setApproveMargin] = useState('');
  const [rejectedMargin, setRejectedMargin] = useState('');
  const [isRejctedQuantityChange, setIsRejctedQuantityChange] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [originalQty, setOriginalQty] = useState(selectedQC?.original_qty || selectedQC?.quantity || 0);
  const [sampleQty, setSampleQty] = useState(selectedQC?.sample_quantity || 0);
  const [onHoldQty, setOnHoldQty] = useState();
  const [maxAllowedQuantity, setMaxAllowedQuantity] = useState(selectedQC?.original_qty || selectedQC?.quantity || 0);
  const [isOriginalQuantityUpdated, setIsOriginalQuantityUpdated] = useState(false);
  const [defaultAssignees, setDefaultAssignees] = useState([]);
  const [approvedBatchCfs, setApprovedBatchCfs] = useState([]);
  const [rejectedBatchCfs, setRejectedBatchCfs] = useState([]);
  const [approvedInventoryLocationId, setApprovedInventoryLocationId] = useState(selectedQC?.approved_batch?.inventory_location_id ?? '');
  const [rejectedInventoryLocationId, setRejectedInventoryLocationId] = useState(selectedQC?.rejected_batch?.inventory_location_id ?? '');

  useEffect(() => {
    const payload = {
      orgId: user?.tenant_info?.org_id,
      entityName: 'GOOD_RECEIVING_NOTES,BATCH',
    };
    getDocCFV2(payload);
  }, []);

  let manufacturingDateFormat;
  let expiryDateFormat;
  if (selectedGRN) {
    manufacturingDateFormat = selectedQC?.product_sku_info?.manufacturing_date_format;
    expiryDateFormat = selectedQC?.product_sku_info?.expiry_date_format;
  } else {
    manufacturingDateFormat = selectedMO ? selectedQC?.tenant_product_info?.manufacturing_date_format : selectedQC?.entity_info?.manufacturing_date_format;
    expiryDateFormat = selectedMO ? selectedQC?.tenant_product_info?.expiry_date_format : selectedQC?.entity_info?.expiry_date_format;
  }

  const totalQty = ((Number(selectedQC?.original_qty) || Number(selectedQC?.quantity)) + Number(selectedQC?.sample_quantity));
  const enableQcSampling = user.tenant_info?.quality_control_config?.settings?.enable_sampling_in_qc && selectedQC?.quality_control_rules?.control_per !== 'QUANTITY';
  const enableOnHoldInQC = user.tenant_info?.quality_control_config?.settings?.enable_on_hold_in_qc && selectedQC?.quality_control_rules?.control_per !== 'QUANTITY' && selectedQC?.status !== 'COMPLETED';

  const destDepartmentIdForBatchesList = selectedGRN ? selectedGRN?.tenant_info?.default_store_id : selectedMO?.tenant_department_id;

  const handleSampleQtyChange = (e) => {
    let newSampleQty = Number(e.target.value) || 0;
    if (newSampleQty > totalQty) {
      newSampleQty = totalQty;
    }
    const newOriginalQty = totalQty - newSampleQty;
    setSampleQty(newSampleQty);
    setOriginalQty(newOriginalQty);
    setApprovedQty(newOriginalQty);
    setRejectedQty(0);
    setOnHoldQty(0);
    setMaxAllowedQuantity(newOriginalQty);
    setIsOriginalQuantityUpdated(true);
  };

  const handleOnHoldQtyChange = (e) => {
    let newOnHoldQty = Number(e.target.value) || 0;
    if (newOnHoldQty > (totalQty - sampleQty)) {
      newOnHoldQty = totalQty - sampleQty;
    }
    const newOriginalQty = Number(totalQty) - (Number(newOnHoldQty) + Number(sampleQty));
    setOnHoldQty(newOnHoldQty);
    setOriginalQty(newOriginalQty);
    setApprovedQty(newOriginalQty);
    setRejectedQty(0);
    setMaxAllowedQuantity(newOriginalQty);
    setIsOriginalQuantityUpdated(true);
  };

  const handleFullQuantity = (value) => {
    let newOnHoldQty = value || 0;
    if (newOnHoldQty > (totalQty - sampleQty)) {
      newOnHoldQty = totalQty - sampleQty;
    }
    const newOriginalQty = Number(totalQty) - (Number(newOnHoldQty) + Number(sampleQty));
    setOnHoldQty(newOnHoldQty);
    setOriginalQty(newOriginalQty);
    setApprovedQty(newOriginalQty);
    setRejectedQty(0);
    setMaxAllowedQuantity(newOriginalQty);
    setIsOriginalQuantityUpdated(true);
  };

  const normalFlowOfQC = selectedQC?.control_per === 'PRODUCT' && selectedQC?.qc_verification_type === 'PASS_FAIL';
  const qcRuleType = selectedQC?.control_per === 'QUANTITY' && selectedQC?.qc_verification_type === 'PASS_FAIL';

  const hasQCEditable = selectedQC?.is_qc_editable;
  const hasQCCompleted = selectedQC?.status === 'COMPLETED' || selectedQC?.is_qc_on_hold;
  const batchConfig = org?.organisation?.[0]?.inventory_config?.settings?.batch_management;
  const automaticArNumberEnabled = user?.tenant_info?.inventory_config?.settings?.enable_auto_generation_of_ar_number;
  const selectedApproverIsEnable = user.tenant_info?.quality_control_config?.sub_modules?.quality_check?.settings?.only_assignees_can_approve_quality_check;
  const userId = user?.user_id;
  const isApprove = (selectedApproverIsEnable && defaultAssignees?.length > 0) ? defaultAssignees?.includes(userId) : true;

  useEffect(() => {
    setCopySelectedQC(selectedQC);
    setApprovedQty(selectedQC?.status === 'COMPLETED' ? selectedQC?.approved_qty : selectedQC?.original_qty);
    setRejectedQty(selectedQC?.rejected_qty);
    setAdditionalInputsData(selectedQC?.additional_inputs?.map((additionalInputs) => ({ ...additionalInputs, key: uuidv4() })));
  }, []);

  useEffect(() => {
    const updatedBatchesRejected = CustomFieldHelpers.getCfStructure(copySelectedQC?.rejected_batch?.custom_fields, true);
    const updatedBatchesApproved = CustomFieldHelpers.getCfStructure(copySelectedQC?.approved_batch?.custom_fields, true);
    const batchCfs = CustomFieldHelpers.getCfStructure(cfV2DocGoodReceivingNotes?.data?.batch_custom_fields, true);
    setRejectedBatchCfs(updatedBatchesRejected?.length > 0 ? updatedBatchesRejected : batchCfs);
    setApprovedBatchCfs(updatedBatchesApproved?.length > 0 ? updatedBatchesApproved : batchCfs);
    if (selectedMO || selectedGRN) {
      setNewSelectedBatch(copySelectedQC?.rejected_batch ? copySelectedQC?.rejected_batch : copySelectedQC?.available_batches?.find((item) => item?.batch_id === copySelectedQC?.rejected_batch_id));
      selectedGRN ? (setNextBatchCode(`${copySelectedQC?.product_sku_info?.internal_sku_code}/${dayjs().format('DDMMYY')}/${copySelectedQC?.product_sku_info?.product_batch_counter}`))
        : (setNextBatchCode(`${copySelectedQC?.tenant_product_info?.internal_sku_code}/${dayjs().format('DDMMYY')}/${copySelectedQC?.tenant_product_info?.product_batch_counter}`));
      setAvailableBatches(copySelectedQC?.rejected_batch ? [{ ...copySelectedQC?.rejected_batch }, ...(copySelectedQC?.available_batches?.filter((availableBatch) => availableBatch?.is_rejected_batch === true) || [])] : (copySelectedQC?.available_batches?.filter((availableBatch) => availableBatch?.is_rejected_batch === true) || []));
      selectedGRN ? setSelectedLineInfo({
        inventory_location_id: copySelectedQC?.inventory_location_id,
        inventory_location_path: copySelectedQC?.inventory_location_path,
        cost_price: copySelectedQC?.cost_price,
        mrp: copySelectedQC?.mrp,
        lot_number: copySelectedQC?.lot_number,
        expiryDays: copySelectedQC?.product_sku_info?.expiry_days,
        expiry_date: copySelectedQC?.expiry_date,
        batchUom: copySelectedQC?.uom_info?.[0],
        selectedBatch: copySelectedQC?.product_batches,
        uomList: copySelectedQC?.uom_list,
        tenantProductId: copySelectedQC?.tenant_product_id,
      })
        : setSelectedLineInfo({
          cost_price: copySelectedQC?.cost_price,
          mrp: copySelectedQC?.mrp,
          lot_number: copySelectedQC?.lot_number,
          expiry_date: copySelectedQC?.expiry_date,
          expiry_days: copySelectedQC?.tenant_product_info?.expiry_days,
          batchUom: copySelectedQC?.uom_info,
          selectedBatch: copySelectedQC?.product_batches,
          uomList: copySelectedQC?.uom_list,
          tenantProductId: copySelectedQC?.tenant_product_id,
        });
    } else {
      setNewSelectedBatch(copySelectedQC?.entity_info?.available_batches?.find((item) => item?.batch_id === copySelectedQC?.rejected_batch_id));
      setNextBatchCode(`${copySelectedQC?.entity_info?.internal_sku_code}/${dayjs().format('DDMMYY')}/${copySelectedQC?.entity_info?.product_batch_counter}`);
      setAvailableBatches(copySelectedQC?.entity_info?.available_batches?.filter((availableBatch) => availableBatch?.is_rejected_batch) || []);
      setSelectedLineInfo({
        inventory_location_id: copySelectedQC?.approved_batch?.inventory_location_id,
        inventory_location_path: copySelectedQC?.approved_batch?.inventory_location_path,
        cost_price: copySelectedQC?.approved_batch?.cost_price,
        mrp: copySelectedQC?.approved_batch?.mrp,
        lot_number: copySelectedQC?.approved_batch?.lot_number,
        expiry_date: copySelectedQC?.approved_batch?.expiry_date,
        expiry_days: copySelectedQC?.entity_info?.expiry_days,
        batchUom: copySelectedQC?.entity_info?.uom_info,
        selectedBatch: copySelectedQC?.product_batches,
        uomList: copySelectedQC?.entity_info?.uom_list,
        tenantProductId: copySelectedQC?.entity_info?.tenant_product_id,
      });
    }
    if (automaticArNumberEnabled === true || automaticArNumberEnabled === 'true') {
      let arNumberPrefix;
      let initialArNumberCounter;
      if (selectedGRN) {
        arNumberPrefix = copySelectedQC?.product_sku_info?.ar_number_prefix || '';
        initialArNumberCounter = copySelectedQC?.product_sku_info?.ar_number_counter || 0;
      } else if (selectedMO) {
        arNumberPrefix = copySelectedQC?.tenant_product_info?.ar_number_prefix || '';
        initialArNumberCounter = copySelectedQC?.tenant_product_info?.ar_number_counter || 0;
      } else {
        arNumberPrefix = copySelectedQC?.entity_info?.ar_number_prefix || '';
        initialArNumberCounter = copySelectedQC?.entity_info?.ar_number_counter || 0;
      }
      let arNumberCounter = initialArNumberCounter;

      if (rejectedQty && copySelectedQC?.approved_batch?.ar_number === arNumberPrefix + initialArNumberCounter) {
        arNumberCounter += 1;
      }

      const approvedArNumberCheck = arNumberPrefix + arNumberCounter;

      setApproveArNumber(copySelectedQC?.approved_batch?.ar_number || approvedArNumberCheck);

      if (rejectedQty) {
        setRejectedArNumber(copySelectedQC?.rejected_batch?.ar_number || approvedArNumberCheck);
      }
    } else if (copySelectedQC?.status === 'COMPLETED') {
      setApproveArNumber(copySelectedQC?.approved_batch?.ar_number);
      setRejectedArNumber(copySelectedQC?.rejected_batch?.ar_number);
    }
    setApproveLotNumber(copySelectedQC?.approved_batch?.lot_number ? copySelectedQC?.approved_batch?.lot_number : '');
    setRejectedLotNumber(copySelectedQC?.rejected_batch?.lot_number ? copySelectedQC?.rejected_batch?.lot_number : '');
    setApproveMRP(copySelectedQC?.approved_batch?.mrp ? copySelectedQC?.approved_batch?.mrp : '');
    setRejectedMRP(copySelectedQC?.rejected_batch?.mrp ? copySelectedQC?.rejected_batch?.mrp : '');
    setApproveCostPrice(copySelectedQC?.approved_batch?.cost_price ? copySelectedQC?.approved_batch?.cost_price : '');
    setRejectedCostPrice(copySelectedQC?.rejected_batch?.cost_price ? copySelectedQC?.rejected_batch?.cost_price : '');
    setApproveSellingPrice(copySelectedQC?.approved_batch?.selling_price ? copySelectedQC?.approved_batch?.selling_price : '');
    setRejectedSellingPrice(copySelectedQC?.rejected_batch?.selling_price ? copySelectedQC?.rejected_batch?.selling_price : '');
    setApproveRollNumber(copySelectedQC?.approved_batch?.roll_no ? copySelectedQC?.approved_batch?.roll_no : '');
    setRejectedRollNumber(copySelectedQC?.rejected_batch?.roll_no ? copySelectedQC?.rejected_batch?.roll_no : '');
    setApproveLandedCost(copySelectedQC?.approved_batch?.landed_cost ? copySelectedQC?.approved_batch?.landed_cost : '');
    setRejectedLandedCost(copySelectedQC?.rejected_batch?.landed_cost ? copySelectedQC?.rejected_batch?.landed_cost : '');
    setApproveFreightCost(copySelectedQC?.approved_batch?.freight_cost ? copySelectedQC?.approved_batch?.freight_cost : '');
    setRejectedFreightCost(copySelectedQC?.rejected_batch?.freight_cost ? copySelectedQC?.rejected_batch?.freight_cost : '');
    setApproveOtherCost(copySelectedQC?.approved_batch?.other_cost ? copySelectedQC?.approved_batch?.other_cost : '');
    setRejectedOtherCost(copySelectedQC?.rejected_batch?.other_cost ? copySelectedQC?.rejected_batch?.other_cost : '');
    setApproveBranded(copySelectedQC?.approved_batch?.brand ? copySelectedQC?.approved_batch?.brand : '');
    setRejectedBranded(copySelectedQC?.rejected_batch?.brand ? copySelectedQC?.rejected_batch?.brand : '');
    setApproveMfgBatchNumber(copySelectedQC?.approved_batch?.mfg_batch_no ? copySelectedQC?.approved_batch?.mfg_batch_no : '');
    setRejectedMfgBatchNumber(copySelectedQC?.rejected_batch?.mfg_batch_no ? copySelectedQC?.rejected_batch?.mfg_batch_no : '');
    setApproveMfgDate(copySelectedQC?.approved_batch?.manufacturing_date ? copySelectedQC?.approved_batch?.manufacturing_date : '');
    setRejectedMfgDate(copySelectedQC?.rejected_batch?.manufacturing_date ? copySelectedQC?.rejected_batch?.manufacturing_date : '');
    setApproveMargin(copySelectedQC?.approved_batch?.margin ? copySelectedQC?.approved_batch?.margin : '');
    setRejectedMargin(copySelectedQC?.rejected_batch?.margin ? copySelectedQC?.rejected_batch?.margin : '');
    setDefaultAssignees(copySelectedQC?.default_assignees);
    // setSendNotification(copySelectedQC?.send_notification_to_assignees);
  }, [copySelectedQC, cfV2DocGoodReceivingNotes]);

  useEffect(() => {
    if (isRejctedQuantityChange) {
      let newBatchPayload;
      if (selectedMO) {
        newBatchPayload = {
          batch_id: null,
          tenant_product_id: copySelectedQC?.tenant_product_id,
          available_qty: rejectedQty,
          expiry_date: copySelectedQC?.product_sku_info?.expiry_days > 0 ? dayjs(copySelectedQC?.product_sku_info?.expiry_days).format('YYYY-MM-DD') : INFINITE_EXPIRY_DATE,
          lot_number: '',
          custom_batch_number: nextBatchCode,
          cost_price: 0,
          mrp: 0,
          selling_price: 0,
          tenant_department_id: selectedMO?.tenant_department_id,
          uom_id: copySelectedQC?.uom_info?.[0]?.uom_id,
          manual_entry: true,
          inventory_location_id: copySelectedQC?.inventory_location_id,
          inventory_location_path: copySelectedQC?.inventory_location_path,
          is_rejected_batch: true,
        };
      }
      if (selectedGRN) {
        newBatchPayload = {
          batch_id: null,
          tenant_product_id: copySelectedQC?.tenant_product_id,
          available_qty: rejectedQty,
          expiry_date: copySelectedQC?.product_sku_info?.expiry_days > 0 ? dayjs().add(copySelectedQC?.product_sku_info?.expiry_days || 0, 'day').format('YYYY-MM-DD') : copySelectedQC?.expiry_date ? copySelectedQC?.expiry_date : INFINITE_EXPIRY_DATE,
          lot_number: '',
          custom_batch_number: nextBatchCode,
          cost_price: 0,
          mrp: 0,
          selling_price: 0,
          tenant_department_id: selectedGRN?.tenant_info?.default_store_id,
          uom_id: copySelectedQC?.uom_info?.[0]?.uom_id,
          manual_entry: true,
          inventory_location_id: copySelectedQC?.inventory_location_id,
          inventory_location_path: copySelectedQC?.inventory_location_path,
          is_rejected_batch: true,
        };
      } else {
        newBatchPayload = {
          batch_id: null,
          tenant_product_id: copySelectedQC?.entity_info?.tenant_product_id,
          available_qty: rejectedQty,
          expiry_date: copySelectedQC?.entity_info?.expiry_days > 0 ? dayjs(copySelectedQC?.entity_info?.expiry_days).format('YYYY-MM-DD') : INFINITE_EXPIRY_DATE,
          lot_number: '',
          custom_batch_number: nextBatchCode,
          cost_price: 0,
          mrp: 0,
          selling_price: 0,
          tenant_department_id: copySelectedQC?.entity_info?.tenant_department_id,
          uom_id: copySelectedQC?.entity_info?.uom_id,
          manual_entry: true,
          inventory_location_id: copySelectedQC?.inventory_location_id,
          inventory_location_path: copySelectedQC?.inventory_location_path,
          is_rejected_batch: true,
        };
      }
      const entityInfo = {
        ...copySelectedQC.entity_info,
        available_batches: [{ ...newBatchPayload }, ...copySelectedQC?.entity_info?.available_batches || []],
      };
      const newCopySelectedQC = {
        ...copySelectedQC,
        approved_batch: {
          ar_number: approveArNumber,
          cost_price: approveCostPrice,
          mrp: approveMRP,
          selling_price: approveSellingPrice,
          roll_no: approveRollNumber,
          landed_cost: approveLandedCost,
          freight_cost: approveFreightCost,
          other_cost: approveOtherCost,
          brand: approveBranded,
          mfg_batch_no: approveMfgBatchNumber,
          manufacturing_date: approveMfgDate,
          margin: approveMargin,
        },
        entity_info: entityInfo,
        available_batches: [{ ...newBatchPayload }, ...copySelectedQC?.available_batches || []],
      };
      setNewSelectedBatch(newBatchPayload);
      setCopySelectedQC(newCopySelectedQC);
    } else if (Number(rejectedQty) === 0) {
      setNewSelectedBatch(null);
    }
  }, [isRejctedQuantityChange]);

  const setSelectedBatch = (batch) => {
    setNewSelectedBatch(batch);
    setShowLineBatches(false);
    if (selectedGRN) {
      setSelectedLineInfo({
        ...selectedLineInfo,
        expiryDays: copySelectedQC?.product_sku_info?.expiry_days,
        expiry_date: copySelectedQC?.expiry_date,
        batchUom: copySelectedQC?.uom_info,
        uomList: copySelectedQC?.uom_list,
        selectedBatch: batch,
        tenantProductId: copySelectedQC?.tenant_product_id,
      });
    } else {
      selectedMO ? setSelectedLineInfo({
        ...selectedLineInfo,
        expiryDays: copySelectedQC?.expiry_days,
        expiryDate: copySelectedQC?.expiry_date,
        batchUom: copySelectedQC?.uom_info,
        uomList: copySelectedQC?.uom_list,
        selectedBatch: batch,
        tenantProductId: copySelectedQC?.tenant_product_id,
      })
        : setSelectedLineInfo({
          ...selectedLineInfo,
          expiry_days: copySelectedQC?.entity_info?.expiry_days,
          expiry_date: copySelectedQC?.approved_batch?.expiry_date,
          batchUom: copySelectedQC?.entity_info?.uom_info,
          uomList: copySelectedQC?.entity_info?.uom_list,
          selectedBatch: batch,
          tenantProductId: copySelectedQC?.entity_info?.tenant_product_id,
          tenantDepartmentId: copySelectedQC?.entity_info?.tenant_department_id,
        });
    }
    setRejectedArNumber(batch?.ar_number ? batch?.ar_number : rejectedArNumber);
    setRejectedCostPrice(batch?.cost_price ? batch?.cost_price : rejectedCostPrice);
    setRejectedMRP(batch?.mrp ? batch?.mrp : rejectedMRP);
    setRejectedSellingPrice(batch?.selling_price ? batch?.selling_price : rejectedSellingPrice);
    setRejectedRollNumber(batch?.roll_no ? batch?.roll_no : rejectedRollNumber);
    setRejectedLandedCost(batch?.landed_cost ? batch?.landed_cost : rejectedLandedCost);
    setRejectedFreightCost(batch?.freight_cost ? batch?.freight_cost : rejectedFreightCost);
    setRejectedOtherCost(batch?.other_cost ? batch?.other_cost : rejectedOtherCost);
    setRejectedBranded(batch?.brand ? batch?.brand : rejectedBranded);
    setRejectedMfgBatchNumber(batch?.mfg_batch_no ? batch?.mfg_batch_no : rejectedMfgBatchNumber);
    setRejectedMfgDate(batch?.manufacturing_date ? batch?.manufacturing_date : rejectedMfgDate);
    setRejectedMargin(batch?.margin ? batch?.margin : rejectedMargin);
  };

  /**
   *
   * @param batch
   */
  const removeNewBatch = () => {
    if (copySelectedQC?.key === selectedLineInfo?.key) {
      copySelectedQC.selectedBatch = null;
      if (selectedMO || selectedGRN) {
        copySelectedQC.available_batches = availableBatches?.filter((item) => item.batch_id)?.length
          ? [...availableBatches?.filter((item) => item.batch_id)]
          : [];
      } else {
        copySelectedQC.entity_info.available_batches = availableBatches?.filter((item) => item.batch_id)?.length
          ? [...availableBatches?.filter((item) => item.batch_id)]
          : [];
      }
      setSelectedLineInfo({
        ...selectedLineInfo,
        expiryDays: copySelectedQC?.expiryDays,
        batchUom: copySelectedQC?.uom_info,
        selectedBatch: null,
        uomList: copySelectedQC?.uom_list,
        tenantProductId: copySelectedQC?.tenant_product_id,
        key: copySelectedQC?.key,
      });
      setAvailableBatches(copySelectedQC?.entity_info?.available_batches);
      setCopySelectedQC(copySelectedQC);
    }
  };

  /**
   *
   * @param batch
   * @param callback
   */
  const addNewBatch = (batch, callback) => {
    let newCopySelectedQC;
    if (selectedMO) {
      newCopySelectedQC = {
        ...copySelectedQC,
        available_batches: [...copySelectedQC?.available_batches?.filter((item) => item?.is_rejected_batch), { ...batch, is_rejected_batch: true }],
      };
    }
    else if (selectedGRN) {
      newCopySelectedQC = {
        ...copySelectedQC,
        available_batches: [...copySelectedQC?.available_batches || [], { ...batch, is_rejected_batch: true }],
      };
    }
    else {
      const entityInfo = copySelectedQC?.entity_info;
      const newAvailableBatches = [...copySelectedQC?.entity_info?.available_batches || [], { ...batch, is_rejected_batch: true }];
      newCopySelectedQC = {
        ...copySelectedQC,
        entity_info: { ...entityInfo, available_batches: newAvailableBatches },
      };
    }

    callback();
    setCopySelectedQC(newCopySelectedQC);
  };

  /**
   * @param value
   * @param type
   * @param key
   */
  const customInputChange = (type, key) => {
    const newAdditionalInputsData = additionalInputsData.map((additonalInputData) => {
      if (additonalInputData?.key === key) {
        if (additonalInputData?.type === 'ATTACHMENT') {
          return {
            ...additonalInputData,
            value: type?.map((attachment) => ({
              url: attachment?.response?.response?.location || attachment?.url, type: attachment.type, name: attachment.name, uid: attachment.uid,
            })),

          };
        }
        return {
          ...additonalInputData,
          value: type,
        };
      }
      return {
        ...additonalInputData,
      };
    });
    setAdditionalInputsData(newAdditionalInputsData);
  };

  const handleApproveValueChange = (e) => {
    const newValue = Number(e.target.value);
    const originalQtyNumber = Number(originalQty); // Ensure this is a number

    if (newValue <= originalQtyNumber) {
      const newRejected = QUANTITYROUND(maxAllowedQuantity - newValue, selectedQC?.entity_info?.uom_info?.precision);
      setApprovedQty(newValue);
      setRejectedQty(newRejected > 0 ? newRejected : 0);
    } else {
      notification.error({
        message: 'Approved quantity cannot be greater than the original quantity.',
        duration: 4,
        placement: 'top',
      });
    }
  };

  const handleRejectValueChange = (e) => {
    const newValue = Number(e.target.value);
    const maxAllowedQuantityNumber = Number(maxAllowedQuantity); // Ensure this is a number
    const originalQtyNumber = Number(originalQty); // Ensure this is a number

    if (newValue <= originalQtyNumber) {
      const newApproved = QUANTITYROUND((maxAllowedQuantityNumber - newValue), selectedQC?.entity_info?.uom_info?.precision);
      if (newValue) {
        setIsRejctedQuantityChange(true);
      } else {
        setIsRejctedQuantityChange(false);
      }
      setRejectedQty(newValue);
      setApprovedQty(newApproved > 0 ? newApproved : 0);
    } else {
      notification.error({
        message: 'Rejected quantity cannot be greater than the original quantity.',
        duration: 4,
        placement: 'top',
      });
    }
  };

  const transformedAdditionalInputs = additionalInputsData.map((input) => {
    if (input.type === 'DATE' && input.value) {
      return { ...input, value: toISTDate(input.value) };
    }
    return input;
  });

  function validateBatchDetailsSimple(config, batchCheck) {
    // Helper function to check if a field is missing when it's mandatory
    const isFieldValid = (isEnabled, isMandatory, value) => {
      // If the field is enabled and mandatory, check if the value is valid
      if (isEnabled && isMandatory) {
        return !(value === undefined || value === '' || value === 0 || value === null);
      }
      // If the field is not enabled or not mandatory, it's considered valid
      return true;
    };

    if (
      !isFieldValid(config.cost_price_is_enabled, config.cost_price_is_mandatory, batchCheck.cost_price)
      || !isFieldValid(config.lot_number_is_enabled, config.lot_number_is_mandatory, batchCheck.lot_number)
      || !isFieldValid(config.selling_price_is_enabled, config.selling_price_is_mandatory, batchCheck.selling_price)
      || !isFieldValid(config.mrp_is_enabled, config.mrp_is_mandatory, batchCheck.mrp)
      || !isFieldValid(config.ar_number_is_enabled, config.ar_number_is_mandatory, batchCheck.ar_number)
      || !isFieldValid(config.roll_no_is_enabled, config.roll_no_is_mandatory, batchCheck.roll_no)
      || !isFieldValid(config.freight_cost_is_enabled, config.freight_cost_is_mandatory, batchCheck.freight_cost)
      || !isFieldValid(config.other_cost_is_enabled, config.other_cost_is_mandatory, batchCheck.other_cost)
      || !isFieldValid(config.landed_cost_is_enabled, config.landed_cost_is_mandatory, batchCheck.landed_cost)
      || !isFieldValid(config.margin_is_enabled, config.margin_is_mandatory, batchCheck.margin)
      || !isFieldValid(config.brand_is_enabled, config.brand_is_mandatory, batchCheck.brand)
      || !isFieldValid(config.mfg_batch_no_is_enabled, config.mfg_batch_no_is_mandatory, batchCheck.mfg_batch_no)
      || !isFieldValid(config.manufacturing_date_is_enabled, config.manufacturing_date_is_mandatory, batchCheck.manufacturing_date)
      // Include other conditions as needed
    ) {
      // If any field validation fails, return false
      return false;
    }

    // If all batches pass validation, return true
    return true;
  }

  function validateFields(fields) {
    return fields?.every((field) => {
      if (field?.isActive && field?.isRequired) {
        return field?.fieldValue !== undefined && field.fieldValue !== null && field.fieldValue !== '';
      }
      return true;
    });
  }
  const handleUpdateApproveQC = () => {
    let payload;
    setFormSubmitted(true);
    if ((validateFields(approvedBatchCfs) && ((rejectedQty !== null && rejectedQty !== undefined && rejectedQty > 0) ? validateFields(rejectedBatchCfs) : true)) && String(approvedQty) && (rejectedQty > 0 ? !!(selectedBatch?.batch_number || selectedBatch?.custom_batch_number) : true) && ((Number(approvedQty) + rejectedQty) === maxAllowedQuantity) && !additionalInputsData?.filter((additionalInput) => additionalInput.is_required && (additionalInput?.type === 'ATTACHMENT' ? !additionalInput?.value?.length : !additionalInput?.value))?.length) {
      if (selectedMO) {
        payload = {
          tenant_id: selectedMO?.tenant_id,
          quality_check_id: selectedQC?.quality_check_id,
          entity_id: selectedQC?.entity_id,
          entity_line_id: selectedQC?.entity_line_id,
          approved_qty: approvedQty,
          on_hold_quantity: onHoldQty,
          rejected_qty: rejectedQty === null ? 0 : rejectedQty,
          rejected_batch: rejectedQty === 0 ? null : {
            ...selectedBatch,
            tenant_product_id: selectedQC?.tenant_product_info?.tenant_product_id,
            uom_id: selectedQC?.uom_info?.uom_id,
            ar_number: rejectedArNumber,
            expiry_date: selectedBatch?.expiry_date ? FormHelpers.dateFormatter(selectedBatch?.expiry_date, expiryDateFormat) : null,
            tenant_department_id: selectedMO?.tenant_department_id,
            lot_number: rejectedLotNumber || null,
            cost_price: rejectedCostPrice || null,
            mrp: rejectedMRP || null,
            selling_price: rejectedSellingPrice || null,
            roll_no: rejectedRollNumber || null,
            landed_cost: rejectedLandedCost || null,
            freight_cost: rejectedFreightCost || null,
            other_cost: rejectedOtherCost || null,
            brand: rejectedBranded || null,
            mfg_batch_no: rejectedMfgBatchNumber || null,
            manufacturing_date: rejectedMfgDate ? FormHelpers.dateFormatter(rejectedMfgDate, manufacturingDateFormat) : null,
            margin: rejectedMargin || null,
            inventory_location_id: rejectedInventoryLocationId || null,
            custom_fields: CustomFieldHelpers.postCfStructure(rejectedBatchCfs),
          },
          custom_fields: CustomFieldHelpers.postCfStructure(approvedBatchCfs),
          additional_inputs: transformedAdditionalInputs,
          ar_number: approveArNumber || null,
          lot_number: approveLotNumber || null,
          cost_price: approveCostPrice || null,
          mrp: approveMRP || null,
          selling_price: approveSellingPrice || null,
          roll_no: approveRollNumber || null,
          landed_cost: approveLandedCost || null,
          freight_cost: approveFreightCost || null,
          other_cost: approveOtherCost || null,
          brand: approveBranded || null,
          mfg_batch_no: approveMfgBatchNumber || null,
          manufacturing_date: approveMfgDate ? FormHelpers.dateFormatter(approveMfgDate, manufacturingDateFormat) : null,
          margin: approveMargin || null,
          sample_quantity: sampleQty,
          original_qty: originalQty,
          is_quantity_updated: isOriginalQuantityUpdated,
          inventory_location_id: approvedInventoryLocationId || null,
        };
      } else if (selectedGRN) {
        payload = {
          tenant_id: selectedGRN?.grn_tenant_id,
          quality_check_id: selectedQC?.quality_check_id,
          entity_id: selectedQC?.entity_id,
          entity_line_id: selectedQC?.entity_line_id,
          approved_qty: approvedQty,
          on_hold_quantity: onHoldQty,
          rejected_qty: rejectedQty === null ? 0 : rejectedQty,
          rejected_batch: rejectedQty === 0 ? null : {
            ...selectedBatch,
            tenant_product_id: selectedQC?.tenant_product_id,
            uom_id: selectedQC?.uom_info?.[0]?.uom_id,
            ar_number: rejectedArNumber,
            expiry_date: selectedBatch?.expiry_date ? FormHelpers.dateFormatter(selectedBatch?.expiry_date, expiryDateFormat) : null,
            seller_id: selectedQC?.approved_batch?.seller_id,
            seller_name: selectedQC?.approved_batch?.seller_name,
            tenant_department_id: selectedGRN?.tenant_department_id,
            lot_number: rejectedLotNumber || null,
            cost_price: rejectedCostPrice || null,
            mrp: rejectedMRP || null,
            selling_price: rejectedSellingPrice || null,
            roll_no: rejectedRollNumber || null,
            landed_cost: rejectedLandedCost || null,
            freight_cost: rejectedFreightCost || null,
            other_cost: rejectedOtherCost || null,
            brand: rejectedBranded || null,
            mfg_batch_no: rejectedMfgBatchNumber || null,
            manufacturing_date: rejectedMfgDate ? FormHelpers.dateFormatter(rejectedMfgDate, manufacturingDateFormat) : null,
            margin: rejectedMargin || null,
            inventory_location_id: rejectedInventoryLocationId || null,
            custom_fields: CustomFieldHelpers.postCfStructure(rejectedBatchCfs),
          },
          custom_fields: CustomFieldHelpers.postCfStructure(approvedBatchCfs),
          additional_inputs: transformedAdditionalInputs,
          ar_number: approveArNumber || null,
          lot_number: approveLotNumber || null,
          cost_price: approveCostPrice || null,
          mrp: approveMRP || null,
          selling_price: approveSellingPrice || null,
          roll_no: approveRollNumber || null,
          landed_cost: approveLandedCost || null,
          freight_cost: approveFreightCost || null,
          other_cost: approveOtherCost || null,
          brand: approveBranded || null,
          mfg_batch_no: approveMfgBatchNumber || null,
          manufacturing_date: approveMfgDate ? FormHelpers.dateFormatter(approveMfgDate, manufacturingDateFormat) : null,
          margin: approveMargin || null,
          sample_quantity: sampleQty,
          original_qty: originalQty,
          is_quantity_updated: isOriginalQuantityUpdated,
          inventory_location_id: approvedInventoryLocationId || null,
        };
      } else {
        payload = {
          tenant_id: selectedQC?.entity_info?.tenant_id,
          quality_check_id: selectedQC?.quality_check_id,
          entity_id: selectedQC?.entity_id,
          entity_line_id: selectedQC?.entity_line_id,
          approved_qty: approvedQty,
          on_hold_quantity: onHoldQty,
          rejected_qty: rejectedQty === null ? 0 : rejectedQty,
          rejected_batch: rejectedQty === 0 ? null : {
            ...selectedBatch,
            tenant_product_id: selectedQC?.entity_info?.tenant_product_id,
            uom_id: selectedQC?.entity_info?.uom_info?.uom_id,
            ar_number: rejectedArNumber,
            expiry_date: selectedBatch?.expiry_date ? FormHelpers.dateFormatter(selectedBatch?.expiry_date, expiryDateFormat) : null,
            seller_id: selectedQC?.approved_batch?.seller_id,
            seller_name: selectedQC?.approved_batch?.seller_name,
            tenant_department_id: selectedQC?.entity_info?.tenant_department_id,
            lot_number: rejectedLotNumber || null,
            cost_price: rejectedCostPrice || null,
            mrp: rejectedMRP || null,
            selling_price: rejectedSellingPrice || null,
            roll_no: rejectedRollNumber || null,
            landed_cost: rejectedLandedCost || null,
            freight_cost: rejectedFreightCost || null,
            other_cost: rejectedOtherCost || null,
            brand: rejectedBranded || null,
            mfg_batch_no: rejectedMfgBatchNumber || null,
            manufacturing_date: rejectedMfgDate ? FormHelpers.dateFormatter(rejectedMfgDate, manufacturingDateFormat) : null,
            margin: rejectedMargin || null,
            inventory_location_id: rejectedInventoryLocationId || null,
            custom_fields: CustomFieldHelpers.postCfStructure(rejectedBatchCfs),
          },
          custom_fields: CustomFieldHelpers.postCfStructure(approvedBatchCfs),
          additional_inputs: transformedAdditionalInputs,
          ar_number: approveArNumber || null,
          lot_number: approveLotNumber || null,
          cost_price: approveCostPrice || null,
          mrp: approveMRP || null,
          selling_price: approveSellingPrice || null,
          roll_no: approveRollNumber || null,
          landed_cost: approveLandedCost || null,
          freight_cost: approveFreightCost || null,
          other_cost: approveOtherCost || null,
          brand: approveBranded || null,
          mfg_batch_no: approveMfgBatchNumber || null,
          manufacturing_date: approveMfgDate ? FormHelpers.dateFormatter(approveMfgDate, manufacturingDateFormat) : null,
          margin: approveMargin || null,
          sample_quantity: sampleQty,
          original_qty: originalQty,
          is_quantity_updated: isOriginalQuantityUpdated,
          inventory_location_id: approvedInventoryLocationId || null,
        };
      }
      const approveBatchCheck = Number(approvedQty) ? validateBatchDetailsSimple(batchConfig, payload) : true;
      const rejectBatchCheck = Number(rejectedQty) ? validateBatchDetailsSimple(batchConfig, payload?.rejected_batch) : true;
      if (approveBatchCheck && rejectBatchCheck) {
        updateQualityCheck(payload, () => callback());
      }
    }
  };

  const putQualityCheckOnHoldApiCall = (type) => {
    const payload = {
      quality_check_id: selectedQC?.quality_check_id,
      status: type,
    };
    putQualityCheckOnHold(payload, () => callback());
  };

  const handleUpdateApproveQCForQyt = (status) => {
    let payload;
    setFormSubmitted(true);
    if (status === 'APPROVE') {
      setIsApproving(true);
      if ((validateFields(approvedBatchCfs) && ((rejectedQty !== null && rejectedQty !== undefined && rejectedQty > 0) ? validateFields(rejectedBatchCfs) : true)) && String(approvedQty) && (rejectedQty > 0 ? !!(selectedBatch?.batch_number || selectedBatch?.custom_batch_number) : true) && ((Number(approvedQty) + rejectedQty) === maxAllowedQuantity) && !additionalInputsData?.filter((additionalInput) => additionalInput.is_required && (additionalInput?.type === 'ATTACHMENT' ? !additionalInput?.value?.length : !additionalInput?.value))?.length) {
        if (selectedMO) {
          payload = {
            tenant_id: selectedMO?.tenant_id,
            quality_check_id: selectedQC?.quality_check_id,
            entity_id: selectedQC?.entity_id,
            entity_line_id: selectedQC?.entity_line_id,
            approved_qty: 1,
            rejected_qty: 0,
            rejected_batch: rejectedQty === 0 ? null : {
              ...selectedBatch,
              tenant_product_id: selectedQC?.tenant_product_info?.tenant_product_id,
              uom_id: selectedQC?.uom_info?.uom_id,
              ar_number: rejectedArNumber,
              expiry_date: selectedBatch?.expiry_date ? FormHelpers.dateFormatter(selectedBatch?.expiry_date, expiryDateFormat) : null,
              tenant_department_id: selectedMO?.tenant_department_id,
              lot_number: rejectedLotNumber || null,
              cost_price: rejectedCostPrice || null,
              mrp: rejectedMRP || null,
              selling_price: rejectedSellingPrice || null,
              roll_no: rejectedRollNumber || null,
              landed_cost: rejectedLandedCost || null,
              freight_cost: rejectedFreightCost || null,
              other_cost: rejectedOtherCost || null,
              brand: rejectedBranded || null,
              mfg_batch_no: rejectedMfgBatchNumber || null,
              manufacturing_date: rejectedMfgDate ? FormHelpers.dateFormatter(rejectedMfgDate, manufacturingDateFormat) : null,
              margin: rejectedMargin || null,
              custom_fields: CustomFieldHelpers.postCfStructure(rejectedBatchCfs),
            },
            custom_fields: CustomFieldHelpers.postCfStructure(approvedBatchCfs),
            additional_inputs: transformedAdditionalInputs,
            ar_number: approveArNumber || null,
            lot_number: approveLotNumber || null,
            cost_price: approveCostPrice || null,
            mrp: approveMRP || null,
            selling_price: approveSellingPrice || null,
            roll_no: approveRollNumber || null,
            landed_cost: approveLandedCost || null,
            freight_cost: approveFreightCost || null,
            other_cost: approveOtherCost || null,
            brand: approveBranded || null,
            mfg_batch_no: approveMfgBatchNumber || null,
            manufacturing_date: rejectedMfgDate ? FormHelpers.dateFormatter(rejectedMfgDate, manufacturingDateFormat) : null,
            margin: approveMargin || null,
          };
        } else if (selectedGRN) {
          payload = {
            tenant_id: selectedGRN?.grn_tenant_id,
            quality_check_id: selectedQC?.quality_check_id,
            entity_id: selectedQC?.entity_id,
            entity_line_id: selectedQC?.entity_line_id,
            approved_qty: 1,
            rejected_qty: 0,
            rejected_batch: {
              ...selectedBatch,
              tenant_product_id: selectedQC?.tenant_product_id,
              uom_id: selectedQC?.uom_info?.[0]?.uom_id,
              ar_number: rejectedArNumber,
              expiry_date: selectedBatch?.expiry_date ? FormHelpers.dateFormatter(selectedBatch?.expiry_date, expiryDateFormat) : null,
              seller_id: selectedQC?.approved_batch?.seller_id,
              seller_name: selectedQC?.approved_batch?.seller_name,
              tenant_department_id: selectedGRN?.tenant_department_id,
              lot_number: rejectedLotNumber || null,
              cost_price: rejectedCostPrice || null,
              mrp: rejectedMRP || null,
              selling_price: rejectedSellingPrice || null,
              roll_no: rejectedRollNumber || null,
              landed_cost: rejectedLandedCost || null,
              freight_cost: rejectedFreightCost || null,
              other_cost: rejectedOtherCost || null,
              brand: rejectedBranded || null,
              mfg_batch_no: rejectedMfgBatchNumber || null,
              manufacturing_date: rejectedMfgDate ? FormHelpers.dateFormatter(rejectedMfgDate, manufacturingDateFormat) : null,
              margin: rejectedMargin || null,
              custom_fields: CustomFieldHelpers.postCfStructure(rejectedBatchCfs),
            },
            custom_fields: CustomFieldHelpers.postCfStructure(approvedBatchCfs),
            additional_inputs: transformedAdditionalInputs,
            ar_number: approveArNumber || null,
            lot_number: approveLotNumber || null,
            cost_price: approveCostPrice || null,
            mrp: approveMRP || null,
            selling_price: approveSellingPrice || null,
            roll_no: approveRollNumber || null,
            landed_cost: approveLandedCost || null,
            freight_cost: approveFreightCost || null,
            other_cost: approveOtherCost || null,
            brand: approveBranded || null,
            mfg_batch_no: approveMfgBatchNumber || null,
            manufacturing_date: approveMfgDate ? FormHelpers.dateFormatter(approveMfgDate, manufacturingDateFormat) : null,
            margin: approveMargin || null,

          };
        } else {
          payload = {
            tenant_id: selectedQC?.entity_info?.tenant_id,
            quality_check_id: selectedQC?.quality_check_id,
            entity_id: selectedQC?.entity_id,
            entity_line_id: selectedQC?.entity_line_id,
            approved_qty: 1,
            rejected_qty: 0,
            rejected_batch: rejectedQty === 0 ? null : {
              ...selectedBatch,
              tenant_product_id: selectedQC?.entity_info?.tenant_product_id,
              uom_id: selectedQC?.entity_info?.uom_info?.uom_id,
              ar_number: rejectedArNumber,
              expiry_date: selectedBatch?.expiry_date ? FormHelpers.dateFormatter(selectedBatch?.expiry_date, expiryDateFormat) : null,
              seller_id: selectedQC?.approved_batch?.seller_id,
              seller_name: selectedQC?.approved_batch?.seller_name,
              tenant_department_id: selectedQC?.entity_info?.tenant_department_id,
              lot_number: rejectedLotNumber || null,
              cost_price: rejectedCostPrice || null,
              mrp: rejectedMRP || null,
              selling_price: rejectedSellingPrice || null,
              roll_no: rejectedRollNumber || null,
              landed_cost: rejectedLandedCost || null,
              freight_cost: rejectedFreightCost || null,
              other_cost: rejectedOtherCost || null,
              brand: rejectedBranded || null,
              mfg_batch_no: rejectedMfgBatchNumber || null,
              manufacturing_date: rejectedMfgDate ? FormHelpers.dateFormatter(rejectedMfgDate, manufacturingDateFormat) : null,
              margin: rejectedMargin || null,
              custom_fields: CustomFieldHelpers.postCfStructure(rejectedBatchCfs),
            },
            custom_fields: CustomFieldHelpers.postCfStructure(approvedBatchCfs),
            additional_inputs: transformedAdditionalInputs,
            ar_number: approveArNumber || null,
            lot_number: approveLotNumber || null,
            cost_price: approveCostPrice || null,
            mrp: approveMRP || null,
            selling_price: approveSellingPrice || null,
            roll_no: approveRollNumber || null,
            landed_cost: approveLandedCost || null,
            freight_cost: approveFreightCost || null,
            other_cost: approveOtherCost || null,
            brand: approveBranded || null,
            mfg_batch_no: approveMfgBatchNumber || null,
            manufacturing_date: approveMfgDate ? FormHelpers.dateFormatter(approveMfgDate, manufacturingDateFormat) : null,

            margin: approveMargin || null,

          };
        }

        const approveBatchCheck = Number(approvedQty) ? validateBatchDetailsSimple(batchConfig, payload) : true;
        const rejectBatchCheck = Number(rejectedQty) ? validateBatchDetailsSimple(batchConfig, payload?.rejected_batch) : true;
        if (approveBatchCheck && rejectBatchCheck) {
          updateQualityCheck(payload, () => callback());
        }
      }
    } else if (String(approvedQty) && (rejectedQty > 0 ? !!(selectedBatch?.batch_number || selectedBatch?.custom_batch_number) : true) && ((Number(approvedQty) + rejectedQty) === maxAllowedQuantity) && !additionalInputsData?.filter((additionalInput) => additionalInput.is_required && (additionalInput?.type === 'ATTACHMENT' ? !additionalInput?.value?.length : !additionalInput?.value))?.length) {
      setIsRejecting(true);
      if (selectedMO) {
        payload = {
          tenant_id: selectedMO?.tenant_id,
          quality_check_id: selectedQC?.quality_check_id,
          entity_id: selectedQC?.entity_id,
          entity_line_id: selectedQC?.entity_line_id,
          approved_qty: 0,
          rejected_qty: 1,
          rejected_batch: rejectedQty === 0 ? null : {
            ...selectedBatch,
            tenant_product_id: selectedQC?.tenant_product_info?.tenant_product_id,
            uom_id: selectedQC?.uom_info?.uom_id,
            ar_number: rejectedArNumber,
            expiry_date: selectedBatch?.expiry_date ? FormHelpers.dateFormatter(selectedBatch?.expiry_date, expiryDateFormat) : null,
            tenant_department_id: selectedMO?.tenant_department_id,
            lot_number: rejectedLotNumber || null,
            cost_price: rejectedCostPrice || null,
            mrp: rejectedMRP || null,
            selling_price: rejectedSellingPrice || null,
            roll_no: rejectedRollNumber || null,
            landed_cost: rejectedLandedCost || null,
            freight_cost: rejectedFreightCost || null,
            other_cost: rejectedOtherCost || null,
            brand: rejectedBranded || null,
            mfg_batch_no: rejectedMfgBatchNumber || null,
            manufacturing_date: rejectedMfgDate ? FormHelpers.dateFormatter(rejectedMfgDate, manufacturingDateFormat) : null,
            margin: rejectedMargin || null,
            custom_fields: CustomFieldHelpers.postCfStructure(rejectedBatchCfs),
          },
          custom_fields: CustomFieldHelpers.postCfStructure(approvedBatchCfs),
          additional_inputs: transformedAdditionalInputs,
          ar_number: approveArNumber || null,
          lot_number: approveLotNumber || null,
          cost_price: approveCostPrice || null,
          mrp: approveMRP || null,
          selling_price: approveSellingPrice || null,
          roll_no: approveRollNumber || null,
          landed_cost: approveLandedCost || null,
          freight_cost: approveFreightCost || null,
          other_cost: approveOtherCost || null,
          brand: approveBranded || null,
          mfg_batch_no: approveMfgBatchNumber || null,
          manufacturing_date: approveMfgDate ? FormHelpers.dateFormatter(approveMfgDate, manufacturingDateFormat) : null,
          margin: approveMargin || null,

        };
      } else if (selectedGRN) {
        payload = {
          tenant_id: selectedGRN?.grn_tenant_id,
          quality_check_id: selectedQC?.quality_check_id,
          entity_id: selectedQC?.entity_id,
          entity_line_id: selectedQC?.entity_line_id,
          approved_qty: 0,
          rejected_qty: 1,
          rejected_batch: {
            ...selectedBatch,
            tenant_product_id: selectedQC?.tenant_product_id,
            uom_id: selectedQC?.uom_info?.[0]?.uom_id,
            ar_number: rejectedArNumber,
            expiry_date: selectedBatch?.expiry_date ? FormHelpers.dateFormatter(selectedBatch?.expiry_date, expiryDateFormat) : null,
            seller_id: selectedQC?.approved_batch?.seller_id,
            seller_name: selectedQC?.approved_batch?.seller_name,
            tenant_department_id: selectedGRN?.tenant_department_id,
            lot_number: rejectedLotNumber || null,
            cost_price: rejectedCostPrice || null,
            mrp: rejectedMRP || null,
            selling_price: rejectedSellingPrice || null,
            roll_no: rejectedRollNumber || null,
            landed_cost: rejectedLandedCost || null,
            freight_cost: rejectedFreightCost || null,
            other_cost: rejectedOtherCost || null,
            brand: rejectedBranded || null,
            mfg_batch_no: rejectedMfgBatchNumber || null,
            manufacturing_date: rejectedMfgDate ? FormHelpers.dateFormatter(rejectedMfgDate, manufacturingDateFormat) : null,
            margin: rejectedMargin || null,
            custom_fields: CustomFieldHelpers.postCfStructure(rejectedBatchCfs),
          },
          custom_fields: CustomFieldHelpers.postCfStructure(approvedBatchCfs),
          additional_inputs: transformedAdditionalInputs,
          ar_number: approveArNumber || null,
          lot_number: approveLotNumber || null,
          cost_price: approveCostPrice || null,
          mrp: approveMRP || null,
          selling_price: approveSellingPrice || null,
          roll_no: approveRollNumber || null,
          landed_cost: approveLandedCost || null,
          freight_cost: approveFreightCost || null,
          other_cost: approveOtherCost || null,
          brand: approveBranded || null,
          mfg_batch_no: approveMfgBatchNumber || null,
          manufacturing_date: approveMfgDate ? FormHelpers.dateFormatter(approveMfgDate, manufacturingDateFormat) : null,
          margin: approveMargin || null,

        };
      } else {
        payload = {
          tenant_id: selectedQC?.entity_info?.tenant_id,
          quality_check_id: selectedQC?.quality_check_id,
          entity_id: selectedQC?.entity_id,
          entity_line_id: selectedQC?.entity_line_id,
          approved_qty: 0,
          rejected_qty: 1,
          rejected_batch: rejectedQty === 0 ? null : {
            ...selectedBatch,
            tenant_product_id: selectedQC?.entity_info?.tenant_product_id,
            uom_id: selectedQC?.entity_info?.uom_info?.uom_id,
            ar_number: rejectedArNumber,
            expiry_date: selectedBatch?.expiry_date ? FormHelpers.dateFormatter(selectedBatch?.expiry_date, expiryDateFormat) : null,
            seller_id: selectedQC?.approved_batch?.seller_id,
            seller_name: selectedQC?.approved_batch?.seller_name,
            tenant_department_id: selectedQC?.entity_info?.tenant_department_id,
            lot_number: rejectedLotNumber || null,
            cost_price: rejectedCostPrice || null,
            mrp: rejectedMRP || null,
            selling_price: rejectedSellingPrice || null,
            roll_no: rejectedRollNumber || null,
            landed_cost: rejectedLandedCost || null,
            freight_cost: rejectedFreightCost || null,
            other_cost: rejectedOtherCost || null,
            brand: rejectedBranded || null,
            mfg_batch_no: rejectedMfgBatchNumber || null,
            manufacturing_date: rejectedMfgDate ? FormHelpers.dateFormatter(rejectedMfgDate, manufacturingDateFormat) : null,
            margin: rejectedMargin || null,
            custom_fields: CustomFieldHelpers.postCfStructure(rejectedBatchCfs),
          },
          custom_fields: CustomFieldHelpers.postCfStructure(approvedBatchCfs),
          additional_inputs: transformedAdditionalInputs,
          ar_number: approveArNumber || null,
          lot_number: approveLotNumber || null,
          cost_price: approveCostPrice || null,
          mrp: approveMRP || null,
          selling_price: approveSellingPrice || null,
          roll_no: approveRollNumber || null,
          landed_cost: approveLandedCost || null,
          freight_cost: approveFreightCost || null,
          other_cost: approveOtherCost || null,
          brand: approveBranded || null,
          mfg_batch_no: approveMfgBatchNumber || null,
          manufacturing_date: approveMfgDate ? FormHelpers.dateFormatter(approveMfgDate, manufacturingDateFormat) : null,
          margin: approveMargin || null,

        };
      }
      const approveBatchCheck = Number(approvedQty) ? validateBatchDetailsSimple(batchConfig, payload) : true;
      const rejectBatchCheck = Number(rejectedQty) ? validateBatchDetailsSimple(batchConfig, payload?.rejected_batch) : true;
      if (approveBatchCheck && rejectBatchCheck) {
        updateQualityCheck(payload, () => callback());
      }
    }
  };
  const entityName = (selectedQC?.entity_name === 'good_receiving_note' && 'Receiving Status') || (selectedQC?.entity_name === 'manufacturing_order' && 'Order Status') || 'QC Retest';

  const qualityCheck = user?.tenant_info?.quality_control_config?.sub_modules?.quality_check?.is_active;

  const { isDataMaskingPolicyEnable, isHideCostPrice, isHideSellingPrice } = priceMasking;

  let addNewAssignees;
  if (selectedMO) {
    addNewAssignees = {
      quality_check_id: selectedQC?.quality_check_id,
      tenant_id: selectedMO?.tenant_id,
    };
  } else if (selectedGRN) {
    addNewAssignees = {
      quality_check_id: selectedQC?.quality_check_id,
      tenant_id: selectedGRN?.grn_tenant_id,
    };
  } else {
    addNewAssignees = {
      quality_check_id: selectedQC?.quality_check_id,
      tenant_id: selectedQC?.entity_info?.tenant_id,
    };
  }

  const batchCfs = CustomFieldHelpers.getCfStructure(cfV2DocGoodReceivingNotes?.data?.batch_custom_fields, true);

  const splitCfsData = (cfs) => {
    const requiredCfs = cfs?.filter((item) => item.isRequired && item.isActive) || [];
    const nonRequiredCfs = cfs?.filter((item) => !item.isRequired || !item.isActive) || [];

    return { requiredCfs, nonRequiredCfs };
  };

  function updateBatchCfsValues(fieldValue, cfId, type) {
    const updateBatch = (batchData, setBatchData) => {
      const updatedBatch = batchData.map((cf) => (cf.cfId === cfId
        ? {
          ...cf,
          fieldValue:
            cf.fieldType === 'ATTACHMENT'
              ? fieldValue?.map((attachment) => ({
                url: attachment?.response?.response?.location || attachment?.url,
                type: attachment.type,
                name: attachment.name,
                uid: attachment.uid,
              }))
              : fieldValue,
        }
        : cf));
      setBatchData(updatedBatch);
    };

    if (type === 'approved') {
      updateBatch(approvedBatchCfs, setApprovedBatchCfs);
    } else if (type === 'rejected') {
      updateBatch(rejectedBatchCfs, setRejectedBatchCfs);
    }
  }

  const renderBatchCfs = (TempBatchCfs) => TempBatchCfs?.map((cfs) => (
    <div className="ant-row" key={cfs.cfId}>
      <div className="ant-col-md-6" style={{ border: '1px solid #d6e4fc' }}>
        <H3Text text={cfs.fieldName} className="qc-inputs__table-label" required={cfs?.isRequired} />
      </div>
      <div className="ant-col-md-9">
        <CustomFieldLine
          customFields={approvedBatchCfs || []}
          cfId={cfs.cfId}
          labelClassName="orgFormLabel"
          containerClassName="approve-qc__input-row__input"
          inputClassName="orgFormInput input"
          formSubmitted={formSubmitted}
          disabled={updateQualityCheckLoading || hasQCCompleted}
          customLineInputChange={(value) => {
            const matchedObj = value?.find((obj) => obj.cfId === cfs.cfId);
            updateBatchCfsValues(matchedObj?.fieldValue, cfs.cfId, 'approved');
          }}
          fullWidth
          isCarryForward
        />
        {cfs?.isRequired && formSubmitted && (() => {
          // Find the corresponding object in approvedBatchCfs
          const matchedObj = approvedBatchCfs?.find((obj) => obj.cfId === cfs.cfId);

          // Check if the fieldValue is empty
          if (!matchedObj?.fieldValue) {
            return (
              <span style={{
                color: 'red',
                fontSize: '10px',
                fontWeight: 600,
                fontStyle: 'italic',
                marginTop: '5px',
              }}
              >
                *Please enter
                {' '}
                {cfs.fieldName}
              </span>
            );
          }
          return null;
        })()}
      </div>
      {normalFlowOfQC && (
        <div className="ant-col-md-9">
          <CustomFieldLine
            customFields={rejectedBatchCfs || []}
            cfId={cfs.cfId}
            labelClassName="orgFormLabel"
            containerClassName="orgInputContainer approve-qc__input-row__input"
            inputClassName="orgFormInput input"
            formSubmitted={formSubmitted}
            disabled={updateQualityCheckLoading || hasQCCompleted}
            customLineInputChange={(value) => {
              const matchedObj = value?.find((obj) => obj.cfId === cfs.cfId);
              updateBatchCfsValues(matchedObj?.fieldValue, cfs.cfId, 'rejected');
            }}
            fullWidth
            isCarryForward
          />
          {cfs?.isRequired && formSubmitted && (() => {
            // Find the corresponding object in approvedBatchCfs
            const matchedObj = rejectedBatchCfs?.find((obj) => obj.cfId === cfs.cfId);

            // Check if the fieldValue is empty
            if (!matchedObj?.fieldValue) {
              return (
                <span style={{
                  color: 'red',
                  fontSize: '10px',
                  fontWeight: 600,
                  fontStyle: 'italic',
                  marginTop: '5px',
                }}
                >
                  *Please enter
                  {' '}
                  {cfs.fieldName}
                </span>
              );
            }
            return null;
          })()}
        </div>
      )}
    </div>
  ));

  return (
    <Fragment>
      <div className="approve-qc__wrapper">
        <div className="custom-drawer__header-wrapper">
          <div className="custom-drawer__header">
            <div className="approve-qc__header-icon">
              <Lottie
                animationData={qcLottie}
                loop={false}
              />
            </div>
            <div>
              <H3Text text="Quality Inspection Report" className="approve-qc__header-title" />
              <div className="approve-qc__header-content">
                {selectedGRN ? (
                  <H3Text
                    text={<div>{`Goods Receiving Note #${selectedQC?.reference_id}`}</div>}
                    className="approve-qc__header-subtitle"
                  />
                ) : (
                  <H3Text
                    text={<div>{
                      selectedMO ? `Manufacturing Order #${selectedQC?.reference_id}` : `${entityName} #${selectedQC?.reference_id || ''}${selectedQC.is_qc_retest ? selectedQC?.approved_batch?.custom_batch_number : ''}`
                    }</div>}
                    className="approve-qc__header-subtitle"
                  />
                )}
              </div>
            </div>
            <div className="custom-drawer__close-icon" onClick={() => onClose()}>
              <FontAwesomeIcon icon={faCircleXmark} />
            </div>
          </div>
        </div>

        <div className="ant-row">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '99%',
            }}
          >
            <div className="ant-col-md-22">
              <div className="approve-qc-entity__wrapper">
                <H3Text text="Product" className="approve-qc-name" />
                {selectedGRN ? (
                  <H3Text text={`${selectedQC?.product_sku_info?.internal_sku_code} - ${selectedQC?.product_sku_info?.product_sku_name}`} className="approve-qc-value" />
                ) : (
                  <H3Text text={selectedMO ? `${selectedQC?.tenant_product_info?.internal_sku_code} - ${selectedQC?.tenant_product_info?.product_sku_name}` : `${selectedQC?.entity_info?.internal_sku_code} - ${selectedQC?.entity_info?.product_sku_name}`} className="approve-qc-value" />
                )}
              </div>
              <div className="approve-qc-entity__wrapper">
                <H3Text text="Source Location" className="approve-qc-name" />
                <H3Text text={`${selectedQC?.entity_info?.source_info?.tenant_name || ''}${selectedQC?.entity_info?.source_info?.department ? ` (${selectedQC?.entity_info?.source_info?.department})` : ''}`} className="approve-qc-value" />
              </div>
              <div className="approve-qc-entity__wrapper">
                <H3Text text="Batch Quantity" className="approve-qc-name" />
                {selectedGRN ? (
                  <H3Text text={`${QUANTITY((Number(selectedQC?.original_qty || selectedQC?.quantity || 0) + Number(selectedQC?.sample_quantity || 0)))} ${selectedQC?.uom_info?.[0]?.uqc?.toProperCase()}`} className="approve-qc-value" />
                ) : (
                  <H3Text text={selectedMO ? `${QUANTITY((Number(selectedQC?.original_qty || selectedQC?.quantity || 0) + Number(selectedQC?.sample_quantity || 0)))} ${selectedQC?.uom_info?.uqc?.toProperCase()}` : `${QUANTITY((Number(selectedQC?.original_qty || selectedQC?.quantity || 0) + Number(selectedQC?.sample_quantity || 0)))} ${selectedQC?.entity_info?.uom_info?.uqc?.toProperCase()}`} className="approve-qc-value" />
                )}
              </div>
              {enableQcSampling && (
                <div className="approve-qc-entity__wrapper">
                  <H3Text text="Sample Quantity" className="approve-qc-name" />
                  <input
                    type="number"
                    value={sampleQty}
                    onChange={handleSampleQtyChange}
                    className="sample-qyt-input"
                    disabled={updateQualityCheckLoading || hasQCCompleted}
                    onWheel={(e) => e.target.blur()}
                  />
                  {selectedGRN ? (
                    <H3Text text={`${selectedQC?.uom_info?.[0]?.uqc?.toProperCase()}`} className="approve-qc-uom" />
                  ) : (
                    <H3Text text={selectedMO ? `${selectedQC?.uom_info?.uqc?.toProperCase()}` : `${selectedQC?.entity_info?.uom_info?.uqc?.toProperCase()}`} className="approve-qc-uom" />
                  )}
                </div>
              )}
              {enableOnHoldInQC && (
                <React.Fragment>
                  <div className="approve-qc-entity__wrapper">
                    <H3Text text="On Hold Quantity" className="approve-qc-name" />
                    <div>
                      <input
                        type="number"
                        value={onHoldQty}
                        onChange={(e) => handleOnHoldQtyChange(e)}
                        className="sample-qyt-input"
                        disabled={updateQualityCheckLoading || hasQCCompleted}
                        onWheel={(e) => e.target.blur()}
                      />
                      <H3Text
                        text={(
                          <div style={{ marginTop: '2px' }}>
                            Use Full Quantity
                          </div>
                        )}
                        onClick={() => {
                          if (!hasQCCompleted) {
                            handleFullQuantity(totalQty);
                          }
                        }}
                        className="table-subscript"
                      />
                    </div>
                    {selectedGRN ? (
                      <H3Text
                        text={`${selectedQC?.uom_info?.[0]?.uqc?.toProperCase()}`}
                        className="approve-qc-uom"
                        style={{
                          marginTop: '-15px',
                          marginLeft: '-11px',
                        }}
                      />
                    ) : (
                      <H3Text
                        text={selectedMO ? `${selectedQC?.uom_info?.uqc?.toProperCase()}` : `${selectedQC?.entity_info?.uom_info?.uqc?.toProperCase()}`}
                        className="approve-qc-uom"
                        style={{
                          marginTop: '-15px',
                          marginLeft: '-11px',
                        }}
                      />
                    )}
                  </div>
                </React.Fragment>
              )}
              <div className="approve-qc-entity__wrapper">
                <H3Text text={selectedQC?.status !== 'COMPLETED' ? 'Quantity To be Tested' : 'Tested Quantity'} className="approve-qc-name" />
                {selectedGRN ? (
                  <H3Text text={`${originalQty} ${selectedQC?.uom_info?.[0]?.uqc?.toProperCase()}`} className="approve-qc-value" />
                ) : (
                  <H3Text text={selectedMO ? `${originalQty} ${selectedQC?.uom_info?.uqc?.toProperCase()}` : `${originalQty} ${selectedQC?.entity_info?.uom_info?.uqc?.toProperCase()}`} className="approve-qc-value" />
                )}
              </div>
              {['good_receiving_note', 'manufacturing_order'].includes(selectedQC?.entity_name) && !selectedGRN && !selectedMO && (
                <div className="approve-qc-entity__wrapper">
                  <H3Text text={(selectedQC?.entity_name === 'good_receiving_note' && 'Receiving Status') || (selectedQC?.entity_name === 'manufacturing_order' && 'Order Status')} className="approve-qc-name" />
                  <div style={{ color: Helpers.getStatusColor(selectedQC?.entity_info?.status).color }}>
                    <H3Text text={selectedQC?.entity_info?.status?.replaceAll('_', ' ')} className="approve-qc-value" />
                  </div>
                </div>
              )}
              <div className="approve-qc-entity__wrapper">
                <H3Text text="QC Status" className="approve-qc-name" />
                <div style={{ color: Helpers.getStatusColor(selectedQC?.status).color }}>
                  <H3Text text={selectedQC?.is_qc_on_hold ? 'ON HOLD' : selectedQC?.status?.replaceAll('_', ' ')} className="approve-qc-value" />
                </div>
              </div>

              {['good_receiving_note', 'manufacturing_order'].includes(selectedQC?.entity_name) && (
                <div className="approve-qc-entity__wrapper">
                  {Number(selectedQC?.original_qty) === Number(selectedQC?.rejected_qty) && (
                    <React.Fragment>
                      <H3Text text="QC Approval Status" className="approve-qc-name" />
                      <div style={{ color: 'red' }}>
                        <H3Text text="REJECTED" className="approve-qc-value" />
                      </div>
                    </React.Fragment>
                  )}
                  {Number(selectedQC?.original_qty) === Number(selectedQC?.approved_qty) && (
                    <React.Fragment>
                      <H3Text text="QC Approval Status" className="approve-qc-name" />
                      <div style={{ color: 'rgb(33, 194, 93)' }}>
                        <H3Text text="APPROVED" className="approve-qc-value" />
                      </div>
                    </React.Fragment>
                  )}
                  {!(selectedQC?.rejected_qty) && !(selectedQC?.approved_qty) && (
                    <React.Fragment>
                      <H3Text text="QC Approval Status" className="approve-qc-name" />
                      <div style={{ color: 'rgb(220, 170, 6)' }}>
                        <H3Text text="PENDING" className="approve-qc-value" />
                      </div>
                    </React.Fragment>
                  )}
                </div>
              )}
              <div className="orgInputContainer approve-qc-entity__wrapper">
                <React.Fragment>
                  <H3Text text="Default Assignees" className="approve-qc-name" />
                  <div style={{ width: '200px' }}>
                    <SelectAppUser
                      hideTitle
                      labelClassName="orgFormLabel"
                      inputClassName="orgFormInput"
                      selectedUser={defaultAssignees || []}
                      onChange={(value) => setDefaultAssignees(value)}
                      disabled={updateQualityCheckLoading || hasQCCompleted || (!Helpers.getPermission(Helpers.permissionEntities.QUALITY_CHECKS, Helpers.permissionTypes.UPDATE, user))}
                      isMultiple
                      placeholder="Select default assignee.."
                      allUsers
                      maxTagCount="responsive"
                      qcData={addNewAssignees}
                    />
                  </div>
                </React.Fragment>
              </div>

            </div>
            <div>
              {selectedQC?.is_qc_on_hold && (
                <H3Text text="ON HOLD" className="qc-on-hold" />
              )}
            </div>
          </div>
          {/* main Qc form For Batch fields */}
          <div className="ant-col-md-24 approve-qc__body-wrapper">
            <div className="qc-inputs__wrapper">
              <div className="ant-row">
                <div
                  className="ant-col-md-6"
                  style={{
                    backgroundColor: '#f1f6ff', border: '1px solid #d6e4fc',
                  }}
                />
                <div className={normalFlowOfQC ? 'ant-col-md-9' : 'ant-col-md-18'} style={{ border: '1px solid #d6e4fc' }}>
                  {(selectedGRN || selectedMO) ? (
                    <H3Text text={normalFlowOfQC ? 'Approved Stock' : 'Stock Details'} className="qc-inputs__table-header" />
                  ) : (
                    <H3Text text={normalFlowOfQC ? 'Approved Stock' : 'Batch Details'} className="qc-inputs__table-header" />
                  )}
                </div>
                {normalFlowOfQC && (
                  <div className="ant-col-md-9" style={{ border: '1px solid #d6e4fc' }}>
                    <H3Text text="Rejected Stock" className="qc-inputs__table-header" />
                  </div>
                )}
              </div>
              <div className="ant-row">
                {normalFlowOfQC
                  && (
                    <React.Fragment>
                      <div className="ant-col-md-6" style={{ border: '1px solid #d6e4fc' }}>
                        <H3Text text="Quantity" className="qc-inputs__table-label" />
                      </div>
                      <div className={((formSubmitted && ((Number(rejectedQty) + Number(approvedQty)) !== maxAllowedQuantity)) ? 'input-select-error ant-col-md-9' : 'ant-col-md-9')}>
                        <H3FormInput
                          name="a valid quantity"
                          type="number"
                          containerClassName="orgInputContainer approve-qc__input-row__input"
                          labelClassName="orgFormLabel"
                          inputClassName="orgFormInput input"
                          placeholder=""
                          onChange={handleApproveValueChange}
                          value={approvedQty}
                          disabled={updateQualityCheckLoading || hasQCCompleted}
                        />
                      </div>

                      <div className={((formSubmitted && ((Number(rejectedQty) + Number(approvedQty)) !== maxAllowedQuantity)) ? 'input-select-error ant-col-md-9' : 'ant-col-md-9')}>
                        <H3FormInput
                          name="a valid quantity"
                          type="number"
                          containerClassName="orgInputContainer approve-qc__input-row__input"
                          labelClassName="orgFormLabel"
                          inputClassName="orgFormInput input"
                          placeholder=""
                          onChange={handleRejectValueChange}
                          value={rejectedQty}
                          disabled={updateQualityCheckLoading || hasQCCompleted}
                        // showError={((rejectedQty + approvedQty) !== maxAllowedQuantity)}
                        />
                      </div>
                    </React.Fragment>
                  )}
              </div>
              <div className="ant-row">
                <div className="ant-col-md-6" style={{ border: '1px solid #d6e4fc' }}>
                  <H3Text text="Batch Number" className="qc-inputs__table-label" />
                </div>
                <div className="ant-col-md-9">
                  <Button
                    style={{
                      fontSize: '12px',
                      height: '30px',
                      backgroundColor: 'transparent',
                      color: 'black',
                    }}
                    disabled
                  >
                    {(selectedMO || selectedGRN) ? selectedQC?.custom_batch_number : selectedQC?.approved_batch?.custom_batch_number}
                  </Button>

                </div>
                {normalFlowOfQC && (
                  <React.Fragment>
                    <div className={((formSubmitted && (rejectedQty > 0 && !selectedBatch)) ? 'input-select-error ant-col-md-5' : 'ant-col-md-5')}>
                      <Button
                        style={{
                          fontSize: '12px',
                          height: '32px',
                        }}
                        disabled={updateQualityCheckLoading || !rejectedQty || hasQCCompleted}
                      >
                        {(selectedMO || selectedGRN) ? (
                          selectedBatch ? (
                            <div>
                              {selectedBatch.custom_batch_number || selectedBatch.batch_number}
                            </div>
                          ) : (
                            <div style={{ color: '#c3c3c3' }}>Rejected Batch&nbsp;</div>
                          )
                        ) : (
                          (selectedBatch || selectedQC) ? (
                            <div>
                              {selectedBatch?.custom_batch_number ||
                                selectedBatch?.batch_number ||
                                selectedQC?.rejected_batch?.custom_batch_number ||
                                selectedQC?.rejected_batch?.batch_number}
                            </div>
                          ) : (
                            <div style={{ color: '#c3c3c3' }}>Rejected Batch&nbsp;</div>
                          )
                        )}
                      </Button>

                    </div>
                    <div className="ant-col-md-4">
                      <Button
                        style={{
                          fontSize: '12px',
                          height: '32px',
                        }}
                        onClick={() => {
                          setShowLineBatches(true);
                        }}
                        disabled={updateQualityCheckLoading || !rejectedQty || hasQCCompleted}
                      >
                        <div style={{ color: '#c3c3c3' }}>
                          Change Batch&nbsp;
                          <FontAwesomeIcon icon={faSquareUpRight} />
                        </div>
                      </Button>
                    </div>
                  </React.Fragment>
                )}
              </div>
              {/* // new batch columns */}
              {batchConfig?.ar_number_is_enabled && (
                <div className="ant-row">
                  <div className="ant-col-md-6" style={{ border: '1px solid #d6e4fc' }}>
                    <H3Text text="AR Number" className="qc-inputs__table-label" required={batchConfig?.ar_number_is_mandatory} />
                  </div>
                  <div className="ant-col-md-9">
                    <H3FormInput
                      name="AR number"
                      type="text"
                      containerClassName="orgInputContainer approve-qc__input-row__input"
                      labelClassName="orgFormLabel"
                      inputClassName="orgFormInput input"
                      onChange={(e) => {
                        setApproveArNumber(e.target.value);
                      }}
                      value={approveArNumber}
                      disabled={updateQualityCheckLoading || selectedQC?.ar_number || hasQCCompleted}
                      showError={formSubmitted && batchConfig?.ar_number_is_mandatory && !approveArNumber && approvedQty}
                    />
                  </div>
                  {normalFlowOfQC && (
                    <div className="ant-col-md-9">
                      <H3FormInput
                        name="AR number"
                        type="text"
                        containerClassName="orgInputContainer approve-qc__input-row__input"
                        labelClassName="orgFormLabel"
                        inputClassName="orgFormInput input"
                        onChange={(e) => {
                          setRejectedArNumber(e.target.value);
                        }}
                        value={rejectedArNumber}
                        disabled={updateQualityCheckLoading || !rejectedQty || hasQCCompleted}
                        showError={formSubmitted && batchConfig?.lot_number_is_mandatory && !rejectedArNumber && rejectedQty}
                      />
                    </div>
                  )}
                </div>
              )}
              {/* required cfs batch level */}
              {renderBatchCfs(splitCfsData(batchCfs)?.requiredCfs)}
              {/* // new batch columns */}
              {batchConfig?.lot_number_is_enabled && (
                <div className="ant-row">
                  <div className="ant-col-md-6" style={{ border: '1px solid #d6e4fc' }}>
                    <H3Text
                      text="Lot #"
                      className="qc-inputs__table-label"
                      required={batchConfig?.lot_number_is_mandatory}
                    />
                  </div>
                  <div className="ant-col-md-9">
                    <H3FormInput
                      name="Lot number"
                      type="text"
                      containerClassName="orgInputContainer approve-qc__input-row__input"
                      labelClassName="orgFormLabel"
                      inputClassName="orgFormInput input"
                      onChange={(e) => {
                        setApproveLotNumber(e.target.value);
                      }}
                      value={approveLotNumber}
                      disabled={updateQualityCheckLoading || hasQCCompleted}
                      showError={formSubmitted && batchConfig?.lot_number_is_mandatory && !approveLotNumber && approvedQty}
                    />
                  </div>
                  {normalFlowOfQC && (
                    <div className="ant-col-md-9">
                      <H3FormInput
                        name="Lot number"
                        type="text"
                        containerClassName="orgInputContainer approve-qc__input-row__input"
                        labelClassName="orgFormLabel"
                        inputClassName="orgFormInput input"
                        onChange={(e) => {
                          setRejectedLotNumber(e.target.value);
                        }}
                        value={rejectedLotNumber}
                        disabled={updateQualityCheckLoading || !rejectedQty || hasQCCompleted}
                        showError={formSubmitted && batchConfig?.lot_number_is_mandatory && !rejectedLotNumber && rejectedQty}
                      />
                    </div>
                  )}
                </div>
              )}
              {/* // new batch columns */}
              <div className="ant-row">
                <div className="ant-col-md-6" style={{ border: '1px solid #d6e4fc' }}>
                  <H3Text
                    text="Cost Price"
                    className="qc-inputs__table-label"
                    required={batchConfig?.cost_price_is_mandatory}
                  />
                </div>
                <div className="ant-col-md-9">
                  <H3FormInput
                    name="cost price"
                    type="number"
                    containerClassName="orgInputContainer approve-qc__input-row__input"
                    labelClassName="orgFormLabel"
                    inputClassName="orgFormInput input"
                    onChange={(e) => {
                      setApproveCostPrice(e.target.value);
                    }}
                    value={approveCostPrice}
                    disabled={updateQualityCheckLoading || hasQCCompleted}
                    showError={formSubmitted && batchConfig?.cost_price_is_mandatory && !approveCostPrice && approvedQty}
                    hideInput={isDataMaskingPolicyEnable && isHideCostPrice}
                    popOverMessage="You don't have access to view cost price"
                  />
                </div>
                {normalFlowOfQC && (
                  <div className="ant-col-md-9">
                    <H3FormInput
                      name="cost price"
                      type="text"
                      containerClassName="orgInputContainer approve-qc__input-row__input"
                      labelClassName="orgFormLabel"
                      inputClassName="orgFormInput input"
                      onChange={(e) => {
                        setRejectedCostPrice(e.target.value);
                      }}
                      value={rejectedCostPrice}
                      disabled={updateQualityCheckLoading || !rejectedQty || hasQCCompleted}
                      showError={formSubmitted && batchConfig?.cost_price_is_mandatory && !rejectedCostPrice && rejectedQty}
                      hideInput={isDataMaskingPolicyEnable && isHideCostPrice}
                      popOverMessage="You don't have access to view cost price"
                    />
                  </div>
                )}
              </div>

              {/* // new batch columns */}
              {batchConfig?.mrp_is_enabled && (
                <div className="ant-row">
                  <div className="ant-col-md-6" style={{ border: '1px solid #d6e4fc' }}>
                    <H3Text
                      text="MRP"
                      className="qc-inputs__table-label"
                      required={batchConfig?.mrp_is_mandatory}
                    />
                  </div>
                  <div className="ant-col-md-9">
                    <H3FormInput
                      name="mrp"
                      type="number"
                      containerClassName="orgInputContainer approve-qc__input-row__input"
                      labelClassName="orgFormLabel"
                      inputClassName="orgFormInput input"
                      onChange={(e) => {
                        setApproveMRP(e.target.value);
                      }}
                      value={approveMRP}
                      disabled={updateQualityCheckLoading || selectedQC?.mrp || hasQCCompleted}
                      showError={formSubmitted && batchConfig?.mrp_is_mandatory && !approveMRP && approvedQty}
                    />
                  </div>
                  {normalFlowOfQC && (
                    <div className="ant-col-md-9">
                      <H3FormInput
                        name="mrp"
                        type="text"
                        containerClassName="orgInputContainer approve-qc__input-row__input"
                        labelClassName="orgFormLabel"
                        inputClassName="orgFormInput input"
                        onChange={(e) => {
                          setRejectedMRP(e.target.value);
                        }}
                        value={rejectedMRP}
                        disabled={updateQualityCheckLoading || !rejectedQty || hasQCCompleted}
                        showError={formSubmitted && batchConfig?.mrp_is_mandatory && !rejectedMRP && rejectedQty}
                      />
                    </div>
                  )}
                </div>
              )}
              {/* // new batch columns */}
              {batchConfig?.selling_price_is_enabled && (
                <div className="ant-row">
                  <div className="ant-col-md-6" style={{ border: '1px solid #d6e4fc' }}>
                    <H3Text
                      text="Selling Price"
                      className="qc-inputs__table-label"
                      required={batchConfig?.selling_price_is_mandatory}
                    />
                  </div>
                  <div className="ant-col-md-9">
                    <H3FormInput
                      name="selling price"
                      type="number"
                      containerClassName="orgInputContainer approve-qc__input-row__input"
                      labelClassName="orgFormLabel"
                      inputClassName="orgFormInput input"
                      onChange={(e) => {
                        setApproveSellingPrice(e.target.value);
                      }}
                      value={approveSellingPrice}
                      disabled={updateQualityCheckLoading || selectedQC?.selling_price || hasQCCompleted}
                      showError={formSubmitted && batchConfig?.selling_price_is_mandatory && !approveSellingPrice && approvedQty}
                      hideInput={isDataMaskingPolicyEnable && isHideSellingPrice}
                      popOverMessage="You don't have access to view selling price"
                    />
                  </div>
                  {normalFlowOfQC && (
                    <div className="ant-col-md-9">
                      <H3FormInput
                        name="selling price"
                        type="text"
                        containerClassName="orgInputContainer approve-qc__input-row__input"
                        labelClassName="orgFormLabel"
                        inputClassName="orgFormInput input"
                        onChange={(e) => {
                          setRejectedSellingPrice(e.target.value);
                        }}
                        value={rejectedSellingPrice}
                        disabled={updateQualityCheckLoading || !rejectedQty || hasQCCompleted}
                        showError={formSubmitted && batchConfig?.selling_price_is_mandatory && !rejectedSellingPrice && rejectedQty}
                        hideInput={isDataMaskingPolicyEnable && isHideSellingPrice}
                        popOverMessage="You don't have access to view selling price"
                      />
                    </div>
                  )}
                </div>
              )}
              {/* // new batch columns */}
              {batchConfig?.roll_no_is_enabled && (
                <div className="ant-row">
                  <div className="ant-col-md-6" style={{ border: '1px solid #d6e4fc' }}>
                    <H3Text
                      text="Roll No"
                      className="qc-inputs__table-label"
                      required={batchConfig?.roll_no_is_mandatory}
                    />
                  </div>
                  <div className="ant-col-md-9">
                    <H3FormInput
                      name="roll number"
                      type="text"
                      containerClassName="orgInputContainer approve-qc__input-row__input"
                      labelClassName="orgFormLabel"
                      inputClassName="orgFormInput input"
                      onChange={(e) => {
                        setApproveRollNumber(e.target.value);
                      }}
                      value={approveRollNumber}
                      disabled={updateQualityCheckLoading || selectedQC?.roll_no || hasQCCompleted}
                      showError={formSubmitted && batchConfig?.roll_no_is_mandatory && !approveRollNumber && approvedQty}
                    />
                  </div>
                  {normalFlowOfQC && (
                    <div className="ant-col-md-9">
                      <H3FormInput
                        name="roll number"
                        type="text"
                        containerClassName="orgInputContainer approve-qc__input-row__input"
                        labelClassName="orgFormLabel"
                        inputClassName="orgFormInput input"
                        onChange={(e) => {
                          setRejectedRollNumber(e.target.value);
                        }}
                        value={rejectedRollNumber}
                        disabled={updateQualityCheckLoading || !rejectedQty || hasQCCompleted}
                        showError={formSubmitted && batchConfig?.roll_no_is_mandatory && !rejectedRollNumber && rejectedQty}
                      />
                    </div>
                  )}
                </div>
              )}
              {/* // new batch columns */}
              {batchConfig?.freight_cost_is_enabled && (
                <div className="ant-row">
                  <div className="ant-col-md-6" style={{ border: '1px solid #d6e4fc' }}>
                    <H3Text
                      text="Freight Cost"
                      className="qc-inputs__table-label"
                      required={batchConfig?.freight_cost_is_mandatory}
                    />
                  </div>
                  <div className="ant-col-md-9">
                    <H3FormInput
                      name="freight cost"
                      type="number"
                      containerClassName="orgInputContainer approve-qc__input-row__input"
                      labelClassName="orgFormLabel"
                      inputClassName="orgFormInput input"
                      onChange={(e) => {
                        setApproveFreightCost(e.target.value);
                      }}
                      value={approveFreightCost}
                      disabled={updateQualityCheckLoading || selectedQC?.ar_number || hasQCCompleted}
                      showError={formSubmitted && batchConfig?.freight_cost_is_mandatory && !approveFreightCost && approvedQty}
                    />
                  </div>
                  {normalFlowOfQC && (
                    <div className="ant-col-md-9">
                      <H3FormInput
                        name="freight cost"
                        type="text"
                        containerClassName="orgInputContainer approve-qc__input-row__input"
                        labelClassName="orgFormLabel"
                        inputClassName="orgFormInput input"
                        onChange={(e) => {
                          setRejectedFreightCost(e.target.value);
                        }}
                        value={rejectedFreightCost}
                        disabled={updateQualityCheckLoading || !rejectedQty || hasQCCompleted}
                        showError={formSubmitted && batchConfig?.freight_cost_is_mandatory && !rejectedFreightCost && rejectedQty}
                      />
                    </div>
                  )}
                </div>
              )}
              {/* // new batch columns */}
              {batchConfig?.other_cost_is_enabled && (
                <div className="ant-row">
                  <div className="ant-col-md-6" style={{ border: '1px solid #d6e4fc' }}>
                    <H3Text
                      text="Other Cost"
                      className="qc-inputs__table-label"
                      required={batchConfig?.other_cost_is_mandatory}
                    />
                  </div>
                  <div className="ant-col-md-9">
                    <H3FormInput
                      name="other cost"
                      type="number"
                      containerClassName="orgInputContainer approve-qc__input-row__input"
                      labelClassName="orgFormLabel"
                      inputClassName="orgFormInput input"
                      onChange={(e) => {
                        setApproveOtherCost(e.target.value);
                      }}
                      value={approveOtherCost}
                      disabled={updateQualityCheckLoading || selectedQC?.other_cost || hasQCCompleted}
                      showError={formSubmitted && batchConfig?.other_cost_is_mandatory && !approveOtherCost && approvedQty}
                    />
                  </div>
                  {normalFlowOfQC && (
                    <div className="ant-col-md-9">
                      <H3FormInput
                        name="other cost"
                        type="text"
                        containerClassName="orgInputContainer approve-qc__input-row__input"
                        labelClassName="orgFormLabel"
                        inputClassName="orgFormInput input"
                        onChange={(e) => {
                          setRejectedOtherCost(e.target.value);
                        }}
                        value={rejectedOtherCost}
                        disabled={updateQualityCheckLoading || !rejectedQty || hasQCCompleted}
                        showError={formSubmitted && batchConfig?.other_cost_is_mandatory && !rejectedOtherCost && rejectedQty}
                      />
                    </div>
                  )}
                </div>
              )}
              {/* // new batch columns */}
              {batchConfig?.margin_is_enabled && (
                <div className="ant-row">
                  <div className="ant-col-md-6" style={{ border: '1px solid #d6e4fc' }}>
                    <H3Text
                      text="Margin"
                      className="qc-inputs__table-label"
                      required={batchConfig?.margin_is_mandatory}
                    />
                  </div>
                  <div className="ant-col-md-9">
                    <H3FormInput
                      name="margin"
                      type="text"
                      containerClassName="orgInputContainer approve-qc__input-row__input"
                      labelClassName="orgFormLabel"
                      inputClassName="orgFormInput input"
                      onChange={(e) => {
                        setApproveMargin(e.target.value);
                      }}
                      value={approveMargin}
                      disabled={updateQualityCheckLoading || selectedQC?.margin || hasQCCompleted}
                      showError={formSubmitted && batchConfig?.margin_is_mandatory && !approveMargin && approvedQty}
                    />
                  </div>
                  {normalFlowOfQC && (
                    <div className="ant-col-md-9">
                      <H3FormInput
                        name="margin"
                        type="text"
                        containerClassName="orgInputContainer approve-qc__input-row__input"
                        labelClassName="orgFormLabel"
                        inputClassName="orgFormInput input"
                        onChange={(e) => {
                          setRejectedMargin(e.target.value);
                        }}
                        value={rejectedMargin}
                        disabled={updateQualityCheckLoading || !rejectedQty || hasQCCompleted}
                        showError={formSubmitted && batchConfig?.margin_is_mandatory && !rejectedMargin && rejectedQty}
                      />
                    </div>
                  )}
                </div>
              )}
              {/* // new batch columns */}
              {batchConfig?.brand_is_enabled && (
                <div className="ant-row">
                  <div className="ant-col-md-6" style={{ border: '1px solid #d6e4fc' }}>
                    <H3Text
                      text="Brand"
                      className="qc-inputs__table-label"
                      required={batchConfig?.brand_is_mandatory}
                    />
                  </div>
                  <div className="ant-col-md-9">
                    <H3FormInput
                      name="brand"
                      type="text"
                      containerClassName="orgInputContainer approve-qc__input-row__input"
                      labelClassName="orgFormLabel"
                      inputClassName="orgFormInput input"
                      onChange={(e) => {
                        setApproveBranded(e.target.value);
                      }}
                      value={approveBranded}
                      disabled={updateQualityCheckLoading || selectedQC?.brand || hasQCCompleted}
                      showError={formSubmitted && batchConfig?.brand_is_mandatory && !approveBranded && approvedQty}
                    />
                  </div>
                  {normalFlowOfQC && (
                    <div className="ant-col-md-9">
                      <H3FormInput
                        name="brand"
                        type="text"
                        containerClassName="orgInputContainer approve-qc__input-row__input"
                        labelClassName="orgFormLabel"
                        inputClassName="orgFormInput input"
                        onChange={(e) => {
                          setRejectedBranded(e.target.value);
                        }}
                        value={rejectedBranded}
                        disabled={updateQualityCheckLoading || !rejectedQty || hasQCCompleted}
                        showError={formSubmitted && batchConfig?.brand_is_mandatory && !rejectedBranded && rejectedQty}
                      />
                    </div>
                  )}
                </div>
              )}
              {/* // new batch columns */}
              {batchConfig?.mfg_batch_no_is_enabled && (
                <div className="ant-row">
                  <div className="ant-col-md-6" style={{ border: '1px solid #d6e4fc' }}>
                    <H3Text
                      text="Mfg Batch No"
                      className="qc-inputs__table-label"
                      required={batchConfig?.mfg_batch_no_is_mandatory}
                    />
                  </div>
                  <div className="ant-col-md-9">
                    <H3FormInput
                      name="Mfg Batch No"
                      type="text"
                      containerClassName="orgInputContainer approve-qc__input-row__input"
                      labelClassName="orgFormLabel"
                      inputClassName="orgFormInput input"
                      onChange={(e) => {
                        setApproveMfgBatchNumber(e.target.value);
                      }}
                      value={approveMfgBatchNumber}
                      disabled={updateQualityCheckLoading || selectedQC?.mfg_batch_no || hasQCCompleted}
                      showError={formSubmitted && batchConfig?.mfg_batch_no_is_mandatory && !approveMfgBatchNumber && approvedQty}
                    />
                  </div>
                  {normalFlowOfQC && (
                    <div className="ant-col-md-9">
                      <H3FormInput
                        name="Mfg Batch No"
                        type="text"
                        containerClassName="orgInputContainer approve-qc__input-row__input"
                        labelClassName="orgFormLabel"
                        inputClassName="orgFormInput input"
                        onChange={(e) => {
                          setRejectedMfgBatchNumber(e.target.value);
                        }}
                        value={rejectedMfgBatchNumber}
                        disabled={updateQualityCheckLoading || !rejectedQty || hasQCCompleted}
                        showError={formSubmitted && batchConfig?.mfg_batch_no_is_mandatory && !rejectedMfgBatchNumber && rejectedQty}
                      />
                    </div>
                  )}
                </div>
              )}
              {/* // new batch columns */}
              {batchConfig?.manufacturing_date_is_enabled && (
                <div className="ant-row">
                  <div className="ant-col-md-6" style={{ border: '1px solid #d6e4fc' }}>
                    <H3Text
                      text="Mfg Date"
                      className="qc-inputs__table-label"
                      required={batchConfig?.manufacturing_date_is_mandatory}
                    />
                  </div>
                  <div className="ant-col-md-9">
                    <DatePicker
                      value={approveMfgDate ? dayjs(approveMfgDate) : null}
                      onChange={(e) => {
                        setApproveMfgDate(e);
                      }}
                      disabled={updateQualityCheckLoading || selectedQC?.manufacturing_date || hasQCCompleted}
                      className={(formSubmitted && batchConfig?.manufacturing_date_is_mandatory && !rejectedMfgDate) ? 'orgFormInputError' : ''}
                      style={{
                        border: 'none',
                        backgroundColor: 'transparent',
                        width: '100%',
                      }}
                      format={manufacturingDateFormat}
                      picker={manufacturingDateFormat !== 'DD/MM/YYYY' ? 'month' : ''}
                    />
                  </div>
                  {normalFlowOfQC && (
                    <div className="ant-col-md-9">
                      <DatePicker
                        value={rejectedMfgDate ? dayjs(rejectedMfgDate) : null}
                        onChange={(e) => {
                          setRejectedMfgDate(e);
                        }}
                        disabled={updateQualityCheckLoading || !rejectedQty || hasQCCompleted}
                        className={`create-grn-table__input-date ${(formSubmitted && batchConfig?.manufacturing_date_is_mandatory && !rejectedMfgDate) ? 'orgFormInputError' : ''}`}
                        style={{
                          border: 'none',
                          backgroundColor: 'transparent',
                          width: '100%',
                        }}
                        format={manufacturingDateFormat}
                        picker={manufacturingDateFormat !== 'DD/MM/YYYY' ? 'month' : ''}
                      />
                    </div>
                  )}
                </div>
              )}
              {/* non required cfs batch level */}
              {renderBatchCfs(splitCfsData(batchCfs)?.nonRequiredCfs)}
              <div className="approve-qc__sub-section2">
                <UpdateQCCustomInputs
                  hideTitle
                  isHorizontalUi
                  additionInputs={additionalInputsData}
                  formSubmitted={formSubmitted}
                  customInputChange={(value, key) => customInputChange(value, key)}
                  containerClassName="ant-row"
                  labelClassName="ant-col-md-6"
                  inputClassName="orgFormInput"
                  disableCase={hasQCCompleted}
                  selectedQC={selectedQC}
                />
              </div>
              <div className="ant-row">
                <div className="ant-col-md-6" style={{ border: '1px solid #d6e4fc' }}>
                  <H3Text
                    text="Inventory Location"
                    className="qc-inputs__table-label"
                  />
                </div>
                {selectedQC?.approved_batch && (
                  <div className="ant-col-md-9">
                    <WarehouseLocationSelector
                      hideTitle
                      selectedInventoryLocation={approvedInventoryLocationId}
                      onChange={(value) => { setApprovedInventoryLocationId(value); }}
                      destDepartmentId={selectedQC?.approved_batch?.tenant_department_id}
                      disabled={updateQualityCheckLoading || !selectedQC?.approved_batch || hasQCCompleted}
                      containerClassName="approve-qc__input-row__input"
                    />
                  </div>
                )}
                {!!(selectedQC?.rejected_batch || rejectedQty) && (
                  <div className="ant-col-md-9">
                    <WarehouseLocationSelector
                      hideTitle
                      selectedInventoryLocation={rejectedInventoryLocationId}
                      onChange={(value) => { setRejectedInventoryLocationId(value); }}
                      destDepartmentId={selectedQC?.rejected_batch?.tenant_department_id}
                      disabled={updateQualityCheckLoading || hasQCCompleted || !(selectedQC?.rejected_batch || rejectedQty)}
                      containerClassName="approve-qc__input-row__input"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {(!hasQCCompleted || selectedQC?.is_qc_on_hold) && !qcRuleType && (selectedMO ? Helpers.getPermission(Helpers.permissionEntities.QUALITY_CHECKS, Helpers.permissionTypes.UPDATE, user) : true) && (
            <div className="approve-qc__footer">
              {Helpers.getPermission(Helpers.permissionEntities.QUALITY_CHECKS, Helpers.permissionTypes.UPDATE, user) && (
                <H3Button
                  text={selectedQC?.is_qc_on_hold ? 'Release from Hold' : 'Create'}
                  buttonType={defaultButtonTypes.BLUE_ROUNDED}
                  onClick={() => {
                    if (selectedQC?.is_qc_on_hold) {
                      putQualityCheckOnHoldApiCall('PENDING');
                    } else {
                      handleUpdateApproveQC();
                    }
                  }}
                  isLoading={updateQualityCheckLoading}
                  disabled={(updateQualityCheckLoading || putQualityCheckOnHoldLoading) || !qualityCheck || !isApprove}
                />
              )}
              {!qualityCheck && (
                <Popconfirm
                  placement="topRight"
                  title="This feature is not accessible within your current plan to use this feature contact us."
                  onConfirm={() => window.Intercom('showNewMessage')}
                  okText="Contact Us"
                  cancelText="Cancel"
                >
                  <img
                    className="barcode-restrict"
                    src={Crown}
                    alt="premium"
                    style={{
                      marginLeft: '8px',
                      marginTop: '5px',
                    }}
                  />
                </Popconfirm>
              )}
            </div>
          )}
          {qcRuleType && (!hasQCCompleted || selectedQC?.is_qc_on_hold) && (
            <div className="approve-qc__footer">
              {Helpers.getPermission(Helpers.permissionEntities.QUALITY_CHECKS, Helpers.permissionTypes.UPDATE, user) && (
                <div
                  style={{
                    display: 'flex',
                  }}
                >
                  <H3Button
                    text="APPROVE"
                    buttonType={defaultButtonTypes.BLUE_ROUNDED}
                    onClick={() => {
                      handleUpdateApproveQCForQyt('APPROVE');
                    }}
                    isLoading={updateQualityCheckLoading && isApproving}
                    disabled={(updateQualityCheckLoading || putQualityCheckOnHoldLoading) || !qualityCheck || !isApprove}
                  />
                  &nbsp;&nbsp;
                  <H3Button
                    text="Reject"
                    buttonType={defaultButtonTypes.BLUE_ROUNDED}
                    style={{ backgroundColor: 'rgb(255, 0, 0)' }}
                    onClick={() => {
                      handleUpdateApproveQCForQyt('REJECT');
                    }}
                    isLoading={updateQualityCheckLoading && isRejecting}
                    disabled={updateQualityCheckLoading || !qualityCheck || !isApprove}
                  />
                </div>
              )}
              {!qualityCheck && (
                <Popconfirm
                  placement="topRight"
                  title="This feature is not accessible within your current plan to use this feature contact us."
                  onConfirm={() => window.Intercom('showNewMessage')}
                  okText="Contact Us"
                  cancelText="Cancel"
                >
                  <img
                    className="barcode-restrict"
                    src={Crown}
                    alt="premium"
                    style={{
                      marginLeft: '8px',
                      marginTop: '5px',
                    }}
                  />
                </Popconfirm>
              )}
            </div>

          )}

        </div>

      </div>
      <Drawer
        open={showLineBatches}
        onClose={() => setShowLineBatches(false)}
        width="400px"
        destroyOnClose
      >
        <div className="custom-drawer__header-wrapper">
          <div className="custom-drawer__header" style={{ width: '355px' }}>
            <H3Text text={(selectedMO || selectedGRN) ? `${selectedQC?.available_batches?.filter((availableBatch) => availableBatch?.is_rejected_batch === true)?.length || '0'} Available Batches` : `${selectedQC?.entity_info?.available_batches?.filter((availableBatch) => availableBatch?.is_rejected_batch)?.length || '0'} Available Batches`} className="custom-drawer__title" />
            <H3Image src={closeIcon} className="custom-drawer__close-icon" onClick={() => setShowLineBatches(false)} />
          </div>
        </div>
        <BatchesList
          batches={availableBatches}
          prefillMrp={selectedLineInfo?.mrp}
          prefillCostPrice={selectedLineInfo?.cost_price}
          prefillLot={selectedLineInfo?.lot_number}
          expiryDays={selectedLineInfo?.expiry_days}
          expiryDate={selectedLineInfo?.expiry_date}
          batchUom={selectedLineInfo?.batchUom}
          selectedBatch={selectedBatch}
          uomList={selectedLineInfo?.uomList}
          tenantProductId={selectedLineInfo?.tenant_product_id}
          setSelectedBatch={(batch) => setSelectedBatch(batch)}
          onAddNewBatch={(batch, cb) => addNewBatch(batch, cb)}
          removeNewBatch={() => removeNewBatch()}
          nextBatchCode={nextBatchCode}
          locationId={selectedLineInfo?.inventory_location_id}
          locationPath={selectedLineInfo?.inventory_location_path}
          destDepartmentId={destDepartmentIdForBatchesList}
        />
      </Drawer>
    </Fragment>
  );
};

const mapStateToProps = ({
  UserReducers, QualityCheckReducers, GRNReducers, MOReducers, CFV2Reducers,
}) => ({
  user: UserReducers.user,
  updateQualityCheckLoading: QualityCheckReducers.updateQualityCheckLoading,
  putQualityCheckOnHoldLoading: QualityCheckReducers.putQualityCheckOnHoldLoading,
  selectedGRN: GRNReducers.selectedGRN,
  selectedMO: MOReducers.selectedMO,
  org: UserReducers.org,
  priceMasking: UserReducers.priceMasking,
  cfV2DocGoodReceivingNotes: CFV2Reducers.cfV2DocGoodReceivingNotes,
});
const mapDispatchToProps = (dispatch) => ({
  updateQualityCheck: (payload, callback) => dispatch(QualityCheckActions.updateQualityCheck(payload, callback)),
  putQualityCheckOnHold: (payload, callback) => dispatch(QualityCheckActions.putQualityCheckOnHold(payload, callback)),
  getDocCFV2: (payload, callback) => dispatch(CFV2Actions.getDocCFV2(payload, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ApproveQC));
